import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Stack, Button, Chip, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, CircularProgress, Alert, Divider,
  ToggleButton, ToggleButtonGroup, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField,
  Tabs, Tab, Badge,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import BoltIcon from '@mui/icons-material/Bolt';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { parseCsvInvoiceFile, formatLabel, type CsvDetectionResult, type ParsedCsvInvoice } from '../utils/csvInvoiceParser';
import { consumePendingStatementUpload } from '../utils/pendingStatementUpload';
import { BRAND } from '../theme/tokens';
import { StatusChip } from '../components/StatusChip';
import { Sparkle } from '../components/Sparkle';

const FI_ORANGE = '#F57C00';
// v1.13 — bumped to v2 when the queue was restructured to enforce a strict
// 1-to-1 mapping between Statements and their reconciliation lists. The v1
// queue could stack rows across statements; the v2 queue requires every row
// to carry a sourceStatementId. Old v1 data is silently dropped.
const STORAGE_KEY = 'bill-importer-queue-v2';
const ABN_SUPPLIER_MAP_KEY = 'bill-importer-abn-supplier-map-v1';
const fmt = (n: number) => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;

const PROCESSING_STEPS = [
  'Uploading invoice to Alfred…',
  'Reading document structure…',
  'Extracting line items…',
  'Matching to PO…',
  'Complete.',
];

// ── Queue status lifecycle ────────────────────────────────────────────────────
type QueueStatus =
  | 'pending'         // just parsed, nothing done yet
  | 'processing'      // Claude/CSV parse in flight
  | 'auto-matched'    // linked to PO by customerPoRef, awaiting review
  | 'needs-review'    // matched but variance >2%
  | 'no-po'           // no confident PO match — user must assign one
  | 'wip'             // user has opened for reconciliation, modifications in progress
  | 'completed'       // bill created, synced, reconciled
  | 'rejected'        // v1.14.4 — user explicitly rejected (disputed / wrong / fraudulent)
  | 'skipped'         // user parked it (duplicate, credit-note, etc.)
  | 'error';          // parse failure

type SourceType = 'pdf' | 'csv-selected-invoices' | 'csv-smartbiz';

interface ParsedBill {
  supplierInvoiceNo: string;
  invoiceDate: string;
  supplierName: string;
  customerPoRef: string;
  subtotalExGst: number;
  totalGst: number;
  totalIncGst: number;
  docType?: 'INV' | 'CRD';
  lines: Array<{
    supplierCode: string;
    description: string;
    qty: number;
    unitPrice: number;
    lineTotal: number;
  }>;
}

interface QueueItem {
  id: string;
  fileName: string;
  source: SourceType;
  status: QueueStatus;
  addedAt: number;               // epoch ms
  supplierId?: string;           // assigned during CSV import
  stepMsg?: string;
  parsed?: ParsedBill;
  matchedPoId?: string;
  matchedPoTotal?: number;
  variance?: number;
  variancePct?: number;
  // v1.14.4 — Soft suggestion when the matcher's only candidate is a low-
  // confidence supplier-name guess. Surfaced as a hint in the Assign dialog
  // but never auto-linked into matchedPoId (which would have caused the
  // "every Quickensted invoice → PO-1648 at -95% variance" false positive).
  suggestedPoId?: string;
  suggestedPoReason?: string;    // human-readable why (e.g. "Only PO for this supplier")
  billId?: string;
  error?: string;
  note?: string;                 // user note added during WIP
  rejectReason?: string;         // v1.14.4 — populated when status === 'rejected'
  // Source-file grouping — every CSV/PDF that lands in the queue is tagged
  // with the original upload-batch's filename so users can filter by
  // "show me only rows from invoices.csv" rather than seeing every batch
  // stacked together.
  sourceFile?: string;
  sourceBatchId?: string;        // unique id for this upload session/batch
  // When the queue item came from a Supplier Statement upload (folded from
  // the legacy /statements/:id reconciliation page), this links it to the
  // parent Statement record so the queue can be filtered by statement.
  sourceStatementId?: string;
}

const STATUS_CFG: Record<QueueStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  pending:        { label: 'Pending',      color: '#888',    bg: '#f5f5f5', icon: <HourglassEmptyIcon sx={{ fontSize: 13 }} /> },
  processing:     { label: 'Processing',   color: '#0277bd', bg: '#e3f2fd', icon: <CircularProgress size={10} thickness={5} /> },
  'auto-matched': { label: 'Auto-Matched', color: '#2e7d32', bg: '#e8f5e9', icon: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> },
  'needs-review': { label: 'Needs Review', color: '#e65100', bg: '#fff3e0', icon: <WarningAmberIcon sx={{ fontSize: 13 }} /> },
  'no-po':        { label: 'Assign PO',    color: '#6a1b9a', bg: '#f3e5f5', icon: <HelpOutlineIcon sx={{ fontSize: 13 }} /> },
  wip:            { label: 'WIP',          color: '#f57c00', bg: '#fff3e0', icon: <PlayArrowIcon sx={{ fontSize: 13 }} /> },
  completed:      { label: 'Completed',    color: '#1b5e20', bg: '#dcedc8', icon: <DoneAllIcon sx={{ fontSize: 13 }} /> },
  rejected:       { label: 'Rejected',     color: '#b71c1c', bg: '#ffebee', icon: <BlockIcon sx={{ fontSize: 13 }} /> },
  skipped:        { label: 'Skipped',      color: '#757575', bg: '#eeeeee', icon: <BlockIcon sx={{ fontSize: 13 }} /> },
  error:          { label: 'Error',        color: '#b71c1c', bg: '#ffebee', icon: <WarningAmberIcon sx={{ fontSize: 13 }} /> },
};

type FilterTab = 'all' | 'todo' | 'wip' | 'completed' | 'rejected' | 'skipped';

// ── Persistence helpers (localStorage) ─────────────────────────────────────────
// Note: File objects aren't serializable, so we only persist the metadata.
// On reload, items show file name but can't be re-parsed without re-upload.
function loadQueue(): QueueItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QueueItem[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function saveQueue(items: QueueItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* quota exceeded — ignore */
  }
}

// ── ABN → supplier memory (remembers which supplier each ABN belongs to) ────
function normaliseAbn(abn: string): string {
  return (abn || '').replace(/\s/g, '').trim();
}
function loadAbnMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(ABN_SUPPLIER_MAP_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function rememberAbnSupplier(abn: string, supplierId: string) {
  const key = normaliseAbn(abn);
  if (!key || !supplierId) return;
  const map = loadAbnMap();
  map[key] = supplierId;
  try { localStorage.setItem(ABN_SUPPLIER_MAP_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}
function lookupAbnSupplier(abn: string): string | undefined {
  return loadAbnMap()[normaliseAbn(abn)];
}

// ─────────────────────────────────────────────────────────────────────────────
export const BillImportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { purchaseOrders, suppliers, products, createBill, updateBill, updatePurchaseOrder, createPurchaseOrder, statements, addStatement, updateStatement, lookupSupplierCode } = useData();

  // ── Statement-context mode ─────────────────────────────────────────────────
  // When the user lands here from /statements via "Open in Bill Importer",
  // the URL carries `?statement=<id>`. We treat the queue as scoped to that
  // statement — banner at the top, filter pre-applied, "Back to Statements"
  // breadcrumb. This is the folded replacement for /statements/:id.
  const statementParam   = searchParams.get('statement');
  const activeStatement  = statementParam ? statements.find(s => s.id === statementParam) ?? null : null;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [queue, setQueueState] = useState<QueueItem[]>(() => loadQueue());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<'bulk' | 'one-by-one'>('bulk');
  const [viewLevel, setViewLevel] = useState<'coarse' | 'detail'>('coarse');
  const [processing, setProcessing] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  // Source-file filter: 'all' or a specific sourceBatchId
  const [filterSource, setFilterSource] = useState<string>('all');
  const [poReviewId, setPoReviewId] = useState<string | null>(null);

  // ── CSV import modal state ───────────────────────────────────────────────────
  const [csvPreview, setCsvPreview] = useState<{
    fileName: string;
    detection: CsvDetectionResult;
    supplierId: string;
  } | null>(null);

  // ── Create-PO-from-Invoice dialog state ──────────────────────────────────────
  // When a no-PO row needs a brand-new PO (supplier billed us but we never
  // raised a PO), we open this dialog to capture the missing Project + Job
  // details before creating the PO and back-linking the queue item.
  const [createPoFromId, setCreatePoFromId] = useState<string | null>(null);
  const [newPoJob, setNewPoJob] = useState<string>('');
  const [newPoProject, setNewPoProject] = useState<string>('');

  // ── Assign-PO dialog state (v1.14.4) ─────────────────────────────────────────
  // Opened from a "no PO" row. Surfaces three explicit lookups in one place:
  // by PO #, by Job #, by Project. Plus a "Create new PO" shortcut. Removes
  // the need for separate buttons cluttering the action cell.
  const [assignPoFromId, setAssignPoFromId] = useState<string | null>(null);

  // ── Reject dialog state (v1.14.4) ───────────────────────────────────────────
  // Asks the user for an optional reason before rejecting an invoice from
  // the queue. The reason is stored on the row for audit / supplier dispute.
  const [rejectFromId, setRejectFromId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');

  // ── Pending files buffer (before parse) ─────────────────────────────────────
  useEffect(() => { saveQueue(queue); }, [queue]);
  const setQueue = useCallback((fnOrItems: QueueItem[] | ((prev: QueueItem[]) => QueueItem[])) => {
    setQueueState(fnOrItems);
  }, []);

  // ── Statement totals reconciler (v1.13, fixed v1.14.2) ───────────────────────
  // Recomputes each Statement's invoiceCount / totalIncGst from queue rows
  // so the Statements list never drifts. Only touches statements that opt-in
  // by setting queueManaged === true (created via ensureStatement below).
  // Seed statements (lines.length > 0 OR queueManaged !== true) own their
  // own metadata and the reconciler leaves them alone — fixes the v1.13 bug
  // where seed counts (18, 7, 4) were silently overwritten with zeros.
  useEffect(() => {
    const byStatement = new Map<string, { count: number; totalIncGst: number }>();
    for (const q of queue) {
      if (!q.sourceStatementId) continue;
      const slot = byStatement.get(q.sourceStatementId) ?? { count: 0, totalIncGst: 0 };
      slot.count += 1;
      slot.totalIncGst += q.parsed?.totalIncGst ?? 0;
      byStatement.set(q.sourceStatementId, slot);
    }
    for (const s of statements) {
      if (!s.queueManaged) continue;            // seed/legacy: hands off
      if (s.lines && s.lines.length > 0) continue;
      const derived = byStatement.get(s.id) ?? { count: 0, totalIncGst: 0 };
      const driftCount   = s.invoiceCount !== derived.count;
      const driftAmount  = Math.abs(s.totalIncGst - derived.totalIncGst) > 0.01;
      if (driftCount || driftAmount) {
        updateStatement(s.id, { invoiceCount: derived.count, totalIncGst: Math.round(derived.totalIncGst * 100) / 100 });
      }
    }
    // We deliberately omit `statements` + `updateStatement` from deps to avoid
    // an infinite update loop — the effect reads them via closure each tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  useEffect(() => {
    fetch('/api/health').then(r => r.json()).then(d => setDemoMode(!d.hasAnthropicKey)).catch(() => setDemoMode(true));
  }, []);

  const poRef = useRef(purchaseOrders);
  useEffect(() => { poRef.current = purchaseOrders; }, [purchaseOrders]);

  const updateItem = useCallback((id: string, patch: Partial<QueueItem>) => {
    setQueue(prev => prev.map(q => q.id === id ? { ...q, ...patch } : q));
  }, [setQueue]);

  // ── PO matcher (tiered, v1.14.4) ─────────────────────────────────────────────
  // High-confidence match requires the supplier's invoice to reference our
  // PO id, PO reference, or job ref. Anything else (supplier name only) is
  // returned as `suggestedPoId` — a soft hint surfaced in the Assign dialog
  // but never auto-linked. This stops the "every Quickensted invoice points
  // to PO-1648 at -95%" false-positive seen on Selected-Invoices imports.
  const matchPo = useCallback((parsed: ParsedBill): {
    poId?: string; poTotal?: number; variance?: number; variancePct?: number;
    status: QueueStatus;
    suggestedPoId?: string; suggestedPoReason?: string;
  } => {
    const pos = poRef.current;
    const ref = (parsed.customerPoRef || '').trim().toLowerCase();
    let po = ref ? pos.find(p => p.id.toLowerCase() === ref) : undefined;
    if (!po && ref) po = pos.find(p => p.reference && p.reference.toLowerCase() === ref);
    if (!po && ref) po = pos.find(p => p.jobRef && p.jobRef.toLowerCase() === ref);

    // Confident hit — link it and grade by variance.
    if (po) {
      const variance = parsed.totalIncGst - po.total;
      const variancePct = po.total > 0 ? (variance / po.total) * 100 : 100;
      const status: QueueStatus = Math.abs(variancePct) <= 2 ? 'auto-matched' : 'needs-review';
      return { poId: po.id, poTotal: po.total, variance, variancePct, status };
    }

    // No confident hit. Compute a soft suggestion (supplier-name only) so
    // the Assign dialog can offer "Did you mean …?" without auto-linking.
    const supName = parsed.supplierName.toLowerCase();
    if (supName) {
      const matchedSup = suppliers.find(s =>
        s.name.toLowerCase().includes(supName.slice(0, 6)) ||
        supName.includes(s.name.toLowerCase().slice(0, 6)));
      if (matchedSup) {
        const candidates = pos.filter(p => p.supplierId === matchedSup.id && p.status !== 'Finalised');
        // Closest by total — better than "first non-finalised".
        let best = candidates[0];
        let bestDelta = Infinity;
        for (const c of candidates) {
          const d = Math.abs(c.total - parsed.totalIncGst);
          if (d < bestDelta) { best = c; bestDelta = d; }
        }
        if (best) {
          return {
            status: 'no-po',
            suggestedPoId: best.id,
            suggestedPoReason: candidates.length === 1
              ? `Only open PO for ${matchedSup.name}`
              : `Closest of ${candidates.length} open POs for ${matchedSup.name} by amount`,
          };
        }
      }
    }

    return { status: 'no-po' };
  }, [suppliers]);

  // ── Parse a single PDF file via Claude Vision ────────────────────────────────
  const parseFile = useCallback(async (id: string, file: File) => {
    updateItem(id, { status: 'processing', stepMsg: PROCESSING_STEPS[0] });

    if (demoMode) {
      for (let i = 0; i < PROCESSING_STEPS.length - 1; i++) {
        await new Promise(r => setTimeout(r, 600 + Math.random() * 400));
        updateItem(id, { stepMsg: PROCESSING_STEPS[i + 1] });
      }
      const pos = poRef.current;
      const po = pos[Math.floor(Math.random() * Math.min(4, pos.length))];
      const isMatch = Math.random() > 0.3;
      const demo: ParsedBill = {
        supplierInvoiceNo: `INV-${Math.floor(10000 + Math.random() * 90000)}`,
        invoiceDate: new Date().toISOString().split('T')[0],
        supplierName: po ? (suppliers.find(s => s.id === po.supplierId)?.name ?? 'Demo Supplier') : 'Unknown Supplier',
        customerPoRef: isMatch && po ? po.id : 'PO-9999',
        subtotalExGst: 200 + Math.random() * 300,
        totalGst: 0,
        totalIncGst: isMatch && po ? po.total * (0.98 + Math.random() * 0.06) : 320,
        lines: [{ supplierCode: 'DEMO-001', description: 'Demo line item', qty: 1, unitPrice: 200, lineTotal: 200 }],
      };
      demo.totalGst = demo.subtotalExGst * 0.1;
      const match = matchPo(demo);
      updateItem(id, { status: match.status, parsed: demo, matchedPoId: match.poId,
        matchedPoTotal: match.poTotal, variance: match.variance, variancePct: match.variancePct,
        suggestedPoId: match.suggestedPoId, suggestedPoReason: match.suggestedPoReason,
        stepMsg: undefined });
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    for (let i = 1; i < PROCESSING_STEPS.length - 1; i++) {
      await new Promise(r => setTimeout(r, 400));
      updateItem(id, { stepMsg: PROCESSING_STEPS[i] });
    }
    try {
      const resp = await fetch('/api/parse-invoice', { method: 'POST', body: fd });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      const parsed: ParsedBill = {
        supplierInvoiceNo: data.supplierInvoiceNo ?? '',
        invoiceDate: data.invoiceDate ?? '',
        supplierName: data.supplierName ?? '',
        customerPoRef: data.customerPoRef ?? '',
        subtotalExGst: data.subtotalExGst ?? 0,
        totalGst: data.totalGst ?? 0,
        totalIncGst: data.totalIncGst ?? 0,
        lines: data.lines ?? [],
      };
      const match = matchPo(parsed);
      updateItem(id, { status: match.status, parsed, matchedPoId: match.poId,
        matchedPoTotal: match.poTotal, variance: match.variance, variancePct: match.variancePct,
        suggestedPoId: match.suggestedPoId, suggestedPoReason: match.suggestedPoReason,
        stepMsg: undefined });
    } catch (err: unknown) {
      updateItem(id, { status: 'error', error: err instanceof Error ? err.message : String(err), stepMsg: undefined });
    }
  }, [demoMode, updateItem, matchPo, suppliers]);

  // ── Store pending File objects in a ref (not serialized) ────────────────────
  const fileBuffer = useRef<Record<string, File>>({});

  // ── 1-to-1 Statement invariant (v1.13) ──────────────────────────────────────
  // Every queue row must belong to exactly one Statement. When the user lands
  // on /bills/import without a ?statement= param and drops files, we lazily
  // create a "Manual upload — DD MMM" statement (one per calendar day, per
  // source kind) and back-link the new queue items to it. The result: the
  // Statements list always reflects every imported invoice, never the other
  // way around.
  const ensureStatement = useCallback((
    kind: 'pdf' | 'csv',
    detection?: { format?: string; supplierAbn?: string; totalIncGst?: number; totalInvoices?: number },
    sourceFileName?: string,
  ): string => {
    if (activeStatement) return activeStatement.id;
    const today = new Date();
    const dayLabel = today.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
    const label = kind === 'csv' ? `Manual CSV upload — ${dayLabel}` : `Manual PDF upload — ${dayLabel}`;
    const existing = statements.find(s =>
      s.filename === label && s.status === 'Active'
    );
    if (existing) return existing.id;
    const source = kind === 'csv'
      ? (detection?.format === 'smartbiz' ? 'csv-smartbiz' as const : 'csv-selected-invoices' as const)
      : 'pdf' as const;
    return addStatement({
      filename: sourceFileName || label,
      source,
      statementDate: today.toISOString().slice(0, 10),
      lines: [],
      status: 'Active',
      invoiceCount: 0,
      totalIncGst: 0,
      queueManaged: true,
      notes: 'Auto-created from a direct Bill Importer upload (no statement was open).',
    });
  }, [activeStatement, statements, addStatement]);

  // Optional bump used immediately on upload (before PDF parsing finishes).
  // The reconciler effect below is the source of truth; this just makes the
  // Statements list refresh feel snappy when a new statement is auto-created.
  const bumpStatementTotals = useCallback((statementId: string, deltaCount: number, deltaIncGst: number) => {
    const s = statements.find(x => x.id === statementId);
    if (!s) return;
    updateStatement(statementId, {
      invoiceCount: Math.max(0, s.invoiceCount + deltaCount),
      totalIncGst: Math.max(0, s.totalIncGst + deltaIncGst),
    });
  }, [statements, updateStatement]);

  // ── Add PDF files to queue ──────────────────────────────────────────────────
  const addPdfFiles = useCallback((files: File[]) => {
    const pdfs = files.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (!pdfs.length) return;
    // 1-to-1 invariant: make sure these files have a parent Statement.
    const statementId = ensureStatement('pdf', undefined, pdfs[0]?.name);
    // One batch ID per drop so all PDFs from the same drop share a source group
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const newItems: QueueItem[] = pdfs.map(f => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      fileBuffer.current[id] = f;
      return { id, fileName: f.name, source: 'pdf', status: 'pending', addedAt: Date.now(),
        sourceFile: f.name, sourceBatchId: batchId, sourceStatementId: statementId };
    });
    setQueue(prev => [...newItems, ...prev]);
    // Pre-bump the statement's count so the Statements list reflects new rows
    // immediately. Totals will catch up once each PDF is parsed (each parse
    // adds the invoice's totalIncGst via parseFile -> updateItem).
    bumpStatementTotals(statementId, newItems.length, 0);
    // If we auto-created the statement (no activeStatement before this drop),
    // push the URL to the scoped view so subsequent uploads land in the same
    // statement and the user sees the 1-to-1 mapping immediately.
    if (!activeStatement) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('statement', statementId);
        return next;
      }, { replace: true });
    }
    if (mode === 'one-by-one') {
      setSelectedId(newItems[0].id);
      const first = newItems[0];
      setTimeout(() => parseFile(first.id, fileBuffer.current[first.id]), 100);
    }
  }, [mode, parseFile, setQueue, ensureStatement, bumpStatementTotals, activeStatement, setSearchParams]);

  // ── Statement PDF parser (v1.14.5) ──────────────────────────────────────────
  // When a PDF is uploaded that represents a multi-invoice supplier statement
  // (the typical Benash / Impact Mechanical monthly statement), routing it
  // through /api/parse-invoice fails — Claude is asked for one invoice but
  // sees thirty rows. This helper routes to /api/parse-statement instead and
  // fans the returned lines out into individual QueueItems, mirroring the
  // CSV-import behaviour. Single-invoice PDFs still work (the statement
  // prompt returns one line in that case).
  const addStatementPdfFile = useCallback(async (file: File) => {
    if (!file) return;
    const statementId = ensureStatement('pdf', undefined, file.name);
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    // Show an immediate "parsing…" placeholder so the user sees activity
    // while Claude reads the document (5–15 s for multi-page statements).
    const placeholderId = `stmt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setQueue(prev => [{
      id: placeholderId, fileName: file.name, source: 'pdf', status: 'processing',
      addedAt: Date.now(), stepMsg: 'Reading multi-invoice statement…',
      sourceFile: file.name, sourceBatchId: batchId, sourceStatementId: statementId,
    }, ...prev]);

    if (!activeStatement) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('statement', statementId);
        return next;
      }, { replace: true });
    }

    // Helper that turns a statement-line shape into our ParsedBill shape +
    // runs the PO matcher to produce a fully-typed QueueItem.
    const lineToQueueItem = (l: { supplierPoRef?: string; supplierRef?: string;
      project?: string; jobRef?: string; amountIncGst?: number; description?: string },
      supplierName: string, statementDate: string): QueueItem => {
      const amount = Number(l.amountIncGst ?? 0);
      const parsed: ParsedBill = {
        supplierInvoiceNo: l.supplierRef || '',
        invoiceDate: statementDate || '',
        supplierName,
        // Pick whichever reference is most likely to match our POs.
        customerPoRef: l.supplierPoRef || l.jobRef || '',
        subtotalExGst: +(amount / 1.1).toFixed(2),
        totalGst: +(amount - amount / 1.1).toFixed(2),
        totalIncGst: amount,
        lines: [{
          supplierCode: '',
          description: l.description?.trim() || [l.supplierPoRef, l.jobRef].filter(Boolean).join(' · ') || 'Statement line',
          qty: 1,
          unitPrice: amount,
          lineTotal: amount,
        }],
      };
      const match = matchPo(parsed);
      return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        fileName: file.name,
        source: 'pdf',
        status: match.status,
        addedAt: Date.now(),
        parsed,
        matchedPoId: match.poId,
        matchedPoTotal: match.poTotal,
        variance: match.variance,
        variancePct: match.variancePct,
        suggestedPoId: match.suggestedPoId,
        suggestedPoReason: match.suggestedPoReason,
        sourceFile: file.name,
        sourceBatchId: batchId,
        sourceStatementId: statementId,
      };
    };

    // ── Demo mode: fabricate 6 fake invoice lines so the flow can be
    //    explored without an Anthropic API key.
    if (demoMode) {
      await new Promise(r => setTimeout(r, 800));
      const fakeLines = Array.from({ length: 6 }, (_, i) => ({
        supplierPoRef: `JB${203000 + Math.floor(Math.random() * 999)}`,
        supplierRef: String(80000 + Math.floor(Math.random() * 9999)),
        amountIncGst: +(200 + Math.random() * 2000).toFixed(2),
        description: `Sale; demo line ${i + 1}`,
      }));
      const newItems = fakeLines.map(l =>
        lineToQueueItem(l, 'Demo Supplier (statement)', new Date().toISOString().slice(0, 10)));
      setQueue(prev => [...newItems, ...prev.filter(q => q.id !== placeholderId)]);
      return;
    }

    // ── Real parse via /api/parse-statement.
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await fetch('/api/parse-statement', { method: 'POST', body: fd });
      const data = await resp.json().catch(() => ({} as Record<string, unknown>));
      if (!resp.ok) {
        const msg =
          (data as { userMessage?: string; error?: string }).userMessage ||
          (data as { error?: string }).error ||
          `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      // v1.15.3 — server can return 200 with a structured AI-failure body
      // (heartbeat streaming locks the status at 200). Surface a friendly
      // message and drop the placeholder so the queue stays clean.
      const aiCode = (data as { code?: string }).code;
      if (aiCode) {
        const friendly = (data as { friendly?: string; error?: string }).friendly
          || (data as { error?: string }).error
          || 'AI parsing is temporarily unavailable.';
        setQueue(prev => prev.filter(q => q.id !== placeholderId));
        alert(`Couldn't parse "${file.name}":\n\n${friendly}`);
        return;
      }
      if (data.error) {
        // Claude returned "Not a supplier statement" — drop the placeholder
        // and prompt the user to use the single-invoice path instead.
        setQueue(prev => prev.filter(q => q.id !== placeholderId));
        alert(
          `"${file.name}" doesn't look like a supplier statement.\n\n` +
          `${data.error}\n\n` +
          `If this is a single supplier invoice (one bill, not a monthly statement), ` +
          `drop it into the Bill Importer queue directly instead of the Statements page.`
        );
        return;
      }
      const lines = (data.lines || []) as Array<Parameters<typeof lineToQueueItem>[0]>;
      if (lines.length === 0) {
        setQueue(prev => prev.filter(q => q.id !== placeholderId));
        alert(`Parsed "${file.name}" but no invoice lines were detected on the statement.`);
        return;
      }
      const supplierName = String(data.supplierName || '');
      const statementDate = String(data.statementDate || '');
      const newItems = lines.map(l => lineToQueueItem(l, supplierName, statementDate));
      setQueue(prev => [...newItems, ...prev.filter(q => q.id !== placeholderId)]);
    } catch (err) {
      setQueue(prev => prev.map(q =>
        q.id === placeholderId
          ? { ...q, status: 'error', error: err instanceof Error ? err.message : String(err) }
          : q
      ));
    }
  }, [demoMode, matchPo, setQueue, ensureStatement, activeStatement, setSearchParams]);

  // ── Handle CSV drop ─────────────────────────────────────────────────────────
  const handleCsvFile = useCallback(async (file: File) => {
    const text = await file.text();
    const detection = parseCsvInvoiceFile(text);
    if (detection.format === 'unknown' || detection.totalInvoices === 0) {
      alert(`Could not detect a known CSV format in "${file.name}". Expected Selected-Invoices or SMARTBIZ format.`);
      return;
    }
    // Try to auto-match supplier by: (1) remembered ABN map, (2) supplier DB ABN match
    let guessSupplier = '';
    if (detection.supplierAbn) {
      const remembered = lookupAbnSupplier(detection.supplierAbn);
      if (remembered && suppliers.some(s => s.id === remembered)) {
        guessSupplier = remembered;
      } else {
        const abnMatch = suppliers.find(s => normaliseAbn(s.abn || '') === normaliseAbn(detection.supplierAbn!));
        if (abnMatch) guessSupplier = abnMatch.id;
      }
    }
    setCsvPreview({ fileName: file.name, detection, supplierId: guessSupplier });
  }, [suppliers]);

  const commitCsvImport = useCallback(() => {
    if (!csvPreview) return;
    const sup = suppliers.find(s => s.id === csvPreview.supplierId);
    if (csvPreview.detection.supplierAbn && csvPreview.supplierId) {
      rememberAbnSupplier(csvPreview.detection.supplierAbn, csvPreview.supplierId);
    }
    const source: SourceType = csvPreview.detection.format === 'smartbiz'
      ? 'csv-smartbiz'
      : 'csv-selected-invoices';

    // 1-to-1 invariant: every CSV invoice gets back-linked to a Statement.
    const statementId = ensureStatement(
      'csv',
      { format: csvPreview.detection.format, supplierAbn: csvPreview.detection.supplierAbn },
      csvPreview.fileName,
    );

    const existingInvoiceNos = new Set(queue.map(q => q.parsed?.supplierInvoiceNo).filter(Boolean));
    // One batch ID for the whole CSV so all its rows can be filtered together
    const batchId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    const newItems: QueueItem[] = csvPreview.detection.invoices.map((inv: ParsedCsvInvoice) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const parsed: ParsedBill = {
        supplierInvoiceNo: inv.supplierInvoiceNo,
        invoiceDate: inv.invoiceDate,
        supplierName: sup?.name ?? '',
        customerPoRef: inv.customerPoRef,
        subtotalExGst: inv.subtotalExGst,
        totalGst: inv.totalGst,
        totalIncGst: inv.totalIncGst,
        docType: inv.docType,
        lines: inv.lines.map(l => ({
          supplierCode: l.supplierCode,
          description: l.description,
          qty: l.qty,
          unitPrice: l.unitPrice,
          lineTotal: l.lineTotal,
        })),
      };
      const isDup = existingInvoiceNos.has(inv.supplierInvoiceNo);
      const match = matchPo(parsed);
      return {
        id,
        fileName: csvPreview.fileName,
        source,
        status: isDup ? 'skipped' : match.status,
        addedAt: Date.now(),
        supplierId: csvPreview.supplierId,
        parsed,
        matchedPoId: match.poId,
        matchedPoTotal: match.poTotal,
        variance: match.variance,
        variancePct: match.variancePct,
        suggestedPoId: match.suggestedPoId,
        suggestedPoReason: match.suggestedPoReason,
        note: isDup ? 'Duplicate invoice # already in queue' : undefined,
        sourceFile: csvPreview.fileName,
        sourceBatchId: batchId,
        sourceStatementId: statementId,
      };
    });

    setQueue(prev => [...newItems, ...prev]);
    // Roll up the freshly parsed totals onto the parent Statement so the
    // Statements list "# Invoices" / "Total inc GST" stays in sync.
    const totalsDelta = newItems.reduce((acc, it) => acc + (it.parsed?.totalIncGst ?? 0), 0);
    bumpStatementTotals(statementId, newItems.length, totalsDelta);
    if (!activeStatement) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('statement', statementId);
        return next;
      }, { replace: true });
    }
    setCsvPreview(null);
  }, [csvPreview, suppliers, queue, matchPo, setQueue, ensureStatement, bumpStatementTotals, activeStatement, setSearchParams]);

  // ── Consume pending statement upload (v1.14.3) ────────────────────────────
  // When the user creates a statement on /statements and clicks "Create &
  // Open", StatementsListPage stashes the File in a module-level slot and
  // navigates here. Pick it up and route to the right parse flow exactly
  // once, only when activeStatement matches.
  const consumedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeStatement) return;
    if (consumedRef.current === activeStatement.id) return;
    const file = consumePendingStatementUpload(activeStatement.id);
    if (!file) return;
    consumedRef.current = activeStatement.id;
    const name = file.name.toLowerCase();
    if (name.endsWith('.csv')) {
      handleCsvFile(file);
    } else if (name.endsWith('.pdf')) {
      // v1.14.5 — PDFs uploaded via the Statements flow are routed through
      // the multi-invoice statement parser so multi-line monthly statements
      // (Impact Mechanical / Benash etc.) fan out into per-invoice rows.
      addStatementPdfFile(file);
    } else {
      if (file.type === 'text/csv') handleCsvFile(file);
      else if (file.type === 'application/pdf') addStatementPdfFile(file);
      else alert(`Unsupported file type for "${file.name}". Drop a PDF or CSV.`);
    }
  }, [activeStatement, handleCsvFile, addPdfFiles, addStatementPdfFile]);

  // ── Bulk process pending PDFs ────────────────────────────────────────────────
  const bulkProcess = useCallback(async () => {
    const pending = queue.filter(q => (q.status === 'pending' || q.status === 'error') && q.source === 'pdf' && fileBuffer.current[q.id]);
    if (!pending.length) return;
    setProcessing(true);
    const CONCURRENCY = 3;
    for (let i = 0; i < pending.length; i += CONCURRENCY) {
      const chunk = pending.slice(i, i + CONCURRENCY);
      await Promise.all(chunk.map(item => parseFile(item.id, fileBuffer.current[item.id])));
    }
    setProcessing(false);
  }, [queue, parseFile]);

  // ── Lifecycle actions ────────────────────────────────────────────────────────
  const applyBill = useCallback((itemId: string) => {
    setQueue(prev => {
      const item = prev.find(q => q.id === itemId);
      if (!item?.parsed || !item.matchedPoId) return prev;
      const po = purchaseOrders.find(p => p.id === item.matchedPoId);
      if (!po) return prev;
      const billId = createBill({ supplierId: po.supplierId, linkedPoId: po.id, jobRef: po.jobRef ?? '' });
      updateBill(billId, {
        supplierInvoiceNo: item.parsed.supplierInvoiceNo,
        date: item.parsed.invoiceDate || new Date().toISOString().split('T')[0],
        totalIncGst: item.parsed.totalIncGst,
        subtotalExGst: item.parsed.subtotalExGst,
        totalGst: item.parsed.totalGst,
        status: 'Draft',
      });
      return prev.map(q => q.id === itemId ? { ...q, status: 'completed', billId } : q);
    });
  }, [purchaseOrders, createBill, updateBill, setQueue]);

  const startReview = useCallback((itemId: string) => {
    updateItem(itemId, { status: 'wip' });
  }, [updateItem]);

  const markCompleted = useCallback((itemId: string) => {
    updateItem(itemId, { status: 'completed' });
  }, [updateItem]);

  const markSkipped = useCallback((itemId: string) => {
    updateItem(itemId, { status: 'skipped' });
  }, [updateItem]);

  // v1.14.4 — Reject a supplier invoice. Used on Needs Review rows when the
  // invoice itself is disputed (wrong qty, wrong price, fraudulent). Unlike
  // "Skipped" (parked for later), Rejected is a terminal stance the user is
  // taking. Captured as its own status + optional rejectReason note.
  const markRejected = useCallback((itemId: string, reason?: string) => {
    updateItem(itemId, { status: 'rejected', rejectReason: reason });
  }, [updateItem]);

  const manuallyLinkPo = useCallback((itemId: string, poId: string | null) => {
    setQueue(prev => prev.map(q => {
      if (q.id !== itemId || !q.parsed) return q;
      if (!poId) {
        return { ...q, status: 'no-po', matchedPoId: undefined, matchedPoTotal: undefined,
          variance: undefined, variancePct: undefined };
      }
      const po = poRef.current.find(p => p.id === poId);
      if (!po) return q;
      const variance = q.parsed.totalIncGst - po.total;
      const variancePct = po.total > 0 ? (variance / po.total) * 100 : 100;
      const status: QueueStatus = Math.abs(variancePct) <= 2 ? 'auto-matched' : 'needs-review';
      return { ...q, status, matchedPoId: po.id, matchedPoTotal: po.total, variance, variancePct };
    }));
  }, [setQueue]);

  /**
   * Create a brand-new PO from a no-PO queue item — the supplier billed us
   * but no PO was ever raised. The newly-created PO inherits the parsed
   * invoice's lines + supplier; the user is asked for Job + Project before
   * confirm. After creation, the queue item is auto-matched to the new PO.
   */
  const openCreatePoDialog = useCallback((itemId: string) => {
    setCreatePoFromId(itemId);
    setNewPoJob('');
    setNewPoProject('');
  }, []);

  const closeCreatePoDialog = useCallback(() => {
    setCreatePoFromId(null);
    setNewPoJob('');
    setNewPoProject('');
  }, []);

  const commitNewPoFromInvoice = useCallback(() => {
    if (!createPoFromId) return;
    const item = queue.find(q => q.id === createPoFromId);
    if (!item || !item.parsed) return;
    const supplierId = item.supplierId
      ?? suppliers.find(s => item.parsed!.supplierName.toLowerCase().includes(s.name.toLowerCase()))?.id
      ?? suppliers[0]?.id ?? '';
    const formattedJob = newPoJob.trim()
      ? (newPoJob.trim().toUpperCase().startsWith('JOB') ? newPoJob.trim() : `JOB #${newPoJob.trim().replace(/[^0-9]/g, '')}`)
      : '';
    const reference = newPoProject.trim() ? `PROJECT: ${newPoProject.trim()}` : '';
    // Map parsed bill lines → POLineDetail. v1.17 — keep the supplier code
    // DISTINCT from the internal inventoryCode. Resolve the internal code via
    // the remembered per-supplier mapping or a direct catalog code match; if
    // neither exists, leave inventoryCode empty so the new PO line surfaces the
    // red "Enter / Select Code" pill (no MISC placeholder).
    const poLines = (item.parsed.lines || []).map(l => {
      const sc = l.supplierCode || '';
      const remembered = sc ? lookupSupplierCode(supplierId, sc) : undefined;
      const direct = sc ? products.find(p => p.code.toLowerCase() === sc.toLowerCase())?.code : undefined;
      return {
        poId: '', // backfilled by createPurchaseOrder
        inventoryCode: remembered || direct || '',
        supplierCode: sc || undefined,
        description: l.description,
        orderedQty: l.qty,
        receivedToDate: 0,
        unitPrice: l.unitPrice,
        discount: 0,
      };
    });
    const newPoId = createPurchaseOrder({ supplierId, jobRef: formattedJob, reference, lines: poLines });
    // Auto-match the queue item to the freshly created PO
    setQueue(prev => prev.map(q => {
      if (q.id !== createPoFromId || !q.parsed) return q;
      const newTotal = q.parsed.totalIncGst;
      return { ...q, status: 'wip', matchedPoId: newPoId, matchedPoTotal: newTotal, variance: 0, variancePct: 0 };
    }));
    closeCreatePoDialog();
  }, [createPoFromId, queue, suppliers, products, lookupSupplierCode, newPoJob, newPoProject, createPurchaseOrder, setQueue, closeCreatePoDialog]);

  const reopenItem = useCallback((itemId: string) => {
    const it = queue.find(q => q.id === itemId);
    if (!it) return;
    // Restore to appropriate status based on match
    let status: QueueStatus = 'pending';
    if (it.matchedPoId && it.variancePct != null) {
      status = Math.abs(it.variancePct) <= 2 ? 'auto-matched' : 'needs-review';
    } else if (it.parsed) {
      status = 'no-po';
    }
    updateItem(itemId, { status });
  }, [queue, updateItem]);

  const removeItem = useCallback((itemId: string) => {
    setQueue(prev => prev.filter(q => q.id !== itemId));
    if (selectedId === itemId) setSelectedId(null);
    delete fileBuffer.current[itemId];
  }, [selectedId, setQueue]);

  // ── Drop handlers ────────────────────────────────────────────────────────────
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const csvs = files.filter(f => f.name.toLowerCase().endsWith('.csv') || f.type === 'text/csv');
    const pdfs = files.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (csvs.length) handleCsvFile(csvs[0]); // process one CSV at a time
    // v1.14.5 — PDFs are routed through the statement parser when scoped to
    // an active statement (multi-invoice statements fan out into per-line
    // rows). Falls back to single-invoice parse when no statement context.
    if (pdfs.length) {
      if (activeStatement) {
        pdfs.forEach(f => addStatementPdfFile(f));
      } else {
        addPdfFiles(pdfs);
      }
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────────
  const statusIs = {
    todo:      (s: QueueStatus) => ['pending', 'processing', 'auto-matched', 'needs-review', 'no-po', 'error'].includes(s),
    wip:       (s: QueueStatus) => s === 'wip',
    completed: (s: QueueStatus) => s === 'completed',
    rejected:  (s: QueueStatus) => s === 'rejected',
    skipped:   (s: QueueStatus) => s === 'skipped',
  };

  const filteredQueue = queue.filter(q => {
    // Statement-context filter overrides the sourceBatchId filter
    if (activeStatement && q.sourceStatementId !== activeStatement.id) return false;
    if (filterSource !== 'all' && q.sourceBatchId !== filterSource) return false;
    if (filterTab === 'all') return true;
    if (filterTab === 'todo')      return statusIs.todo(q.status);
    if (filterTab === 'wip')       return statusIs.wip(q.status);
    if (filterTab === 'completed') return statusIs.completed(q.status);
    if (filterTab === 'rejected')  return statusIs.rejected(q.status);
    if (filterTab === 'skipped')   return statusIs.skipped(q.status);
    return true;
  });

  // Build the unique source-file list for the filter dropdown
  const sourceOptions = useMemo(() => {
    const map = new Map<string, { label: string; count: number; firstAdded: number }>();
    for (const q of queue) {
      const key = q.sourceBatchId ?? 'legacy';
      const label = q.sourceFile ?? q.fileName ?? 'Unknown source';
      const ex = map.get(key);
      if (ex) { ex.count += 1; ex.firstAdded = Math.min(ex.firstAdded, q.addedAt); }
      else map.set(key, { label, count: 1, firstAdded: q.addedAt });
    }
    return Array.from(map.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.firstAdded - a.firstAdded);
  }, [queue]);

  const stats = {
    all:       queue.length,
    todo:      queue.filter(q => statusIs.todo(q.status)).length,
    wip:       queue.filter(q => statusIs.wip(q.status)).length,
    completed: queue.filter(q => statusIs.completed(q.status)).length,
    rejected:  queue.filter(q => statusIs.rejected(q.status)).length,
    skipped:   queue.filter(q => statusIs.skipped(q.status)).length,
    pending:   queue.filter(q => q.status === 'pending').length,
    errors:    queue.filter(q => q.status === 'error').length,
    autoMatched: queue.filter(q => q.status === 'auto-matched').length,
  };
  const selected = queue.find(q => q.id === selectedId) ?? null;

  const supplierName = (id?: string) => suppliers.find(s => s.id === id)?.name ?? '—';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f9f9fb' }}>

      {/* ── Top bar ── */}
      <Box sx={{ px: 2, py: 1.2, backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0',
        display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <IconButton size="small"
          onClick={() => navigate(activeStatement ? '/statements' : '/bills')}
          sx={{ color: '#666' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: BRAND, lineHeight: 1.2 }}>
            {activeStatement ? 'Import Bills from Statement' : 'Supplier Invoice Reconciliation'}
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#777' }}>
            {activeStatement
              ? <>Creating Bills from <strong>{activeStatement.filename}</strong> · {activeStatement.lines.length} parsed line{activeStatement.lines.length !== 1 ? 's' : ''}</>
              : <>Drop bills · match to POs · approve in bulk</>}
          </Typography>
        </Box>
        {/*
          v1.14.6 — Cross-link to the Verify workflow on the same statement.
          The two flows are intentionally separate: Verify checks what we
          have on file (no new bills), Import generates new bills.
        */}
        {activeStatement && (
          <Tooltip title="Switch to Verify mode — check each statement line has a matching PO + Bill (does NOT create new bills)" arrow>
            <Button size="small" variant="outlined"
              onClick={() => navigate(`/statements/${activeStatement.id}`)}
              sx={{ ml: 1, textTransform: 'none', fontSize: '0.7rem', borderColor: BRAND, color: BRAND,
                '&:hover': { backgroundColor: '#f5f0ff' } }}>
              Switch to Verify
            </Button>
          </Tooltip>
        )}
        {activeStatement && (
          <Tooltip title="Show every queue item across all sources (clears statement context)">
            <Button size="small" variant="text"
              onClick={() => { searchParams.delete('statement'); setSearchParams(searchParams); }}
              sx={{ ml: 0.5, textTransform: 'none', fontSize: '0.7rem', color: '#888' }}>
              View full queue
            </Button>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#888', mr: 0.5 }}>Process mode:</Typography>
          <ToggleButtonGroup value={mode} exclusive onChange={(_e, v) => v && setMode(v)} size="small">
            <ToggleButton value="bulk" sx={{ fontSize: '0.65rem', px: 1.2, py: 0.4, gap: 0.5 }}>
              <BoltIcon sx={{ fontSize: 13 }} /> Bulk
            </ToggleButton>
            <ToggleButton value="one-by-one" sx={{ fontSize: '0.65rem', px: 1.2, py: 0.4, gap: 0.5 }}>
              <SkipNextIcon sx={{ fontSize: 13 }} /> One by One
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {queue.some(q => q.parsed) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#888', mr: 0.5 }}>View:</Typography>
            <ToggleButtonGroup value={viewLevel} exclusive onChange={(_e, v) => v && setViewLevel(v)} size="small">
              <Tooltip title="Summary reconciliation table">
                <ToggleButton value="coarse" sx={{ fontSize: '0.65rem', px: 1.2, py: 0.4, gap: 0.5 }}>
                  <ViewStreamIcon sx={{ fontSize: 13 }} /> Summary
                </ToggleButton>
              </Tooltip>
              <Tooltip title="Line-item detail for selected bill">
                <ToggleButton value="detail" sx={{ fontSize: '0.65rem', px: 1.2, py: 0.4, gap: 0.5 }}>
                  <FormatListBulletedIcon sx={{ fontSize: 13 }} /> Detail
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        )}

        {demoMode && (
          <Chip label="Demo mode — no API key" size="small"
            sx={{ fontSize: '0.62rem', backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }} />
        )}
      </Box>

      {/* ── Status filter tabs + source filter ── */}
      {queue.length > 0 && (
        <Box sx={{ px: 2, backgroundColor: '#fff', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tabs value={filterTab} onChange={(_e, v) => setFilterTab(v)}
            sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.7rem', textTransform: 'none' } }}>
            <Tab label={`All · ${stats.all}`}         value="all" />
            <Tab label={<Badge badgeContent={stats.todo} color="primary" sx={{ '& .MuiBadge-badge': { right: -10, fontSize: '0.55rem', height: 14, minWidth: 14 } }}>To Do</Badge>}        value="todo" />
            <Tab label={<Badge badgeContent={stats.wip} color="warning" sx={{ '& .MuiBadge-badge': { right: -10, fontSize: '0.55rem', height: 14, minWidth: 14 } }}>WIP</Badge>}           value="wip" />
            <Tab label={<Badge badgeContent={stats.completed} color="success" sx={{ '& .MuiBadge-badge': { right: -10, fontSize: '0.55rem', height: 14, minWidth: 14 } }}>Completed</Badge>} value="completed" />
            <Tab label={<Badge badgeContent={stats.rejected} color="error" sx={{ '& .MuiBadge-badge': { right: -10, fontSize: '0.55rem', height: 14, minWidth: 14 } }}>Rejected</Badge>} value="rejected" />
            <Tab label={`Skipped · ${stats.skipped}`} value="skipped" />
          </Tabs>
          <Box sx={{ flex: 1 }} />
          {sourceOptions.length > 1 && (
            <Tooltip arrow placement="left" title={
              <Box sx={{ fontSize: '0.7rem' }}>
                <Box sx={{ fontWeight: 700, mb: 0.3 }}>Source filter</Box>
                <Box>Filter the queue by which file these rows came from. Useful when multiple CSV/PDF batches have been imported and you want to focus on one batch at a time.</Box>
              </Box>
            }>
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <Select
                  value={filterSource}
                  onChange={e => setFilterSource(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.72rem', '& .MuiSelect-select': { py: 0.4 } }}>
                  <MenuItem value="all" sx={{ fontSize: '0.72rem' }}>
                    All sources ({queue.length})
                  </MenuItem>
                  {sourceOptions.map(opt => (
                    <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: '0.72rem' }}>
                      <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>{opt.label}</Box>
                      <Box component="span" sx={{ color: '#888' }}>· {opt.count}</Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Tooltip>
          )}
        </Box>
      )}

      {/* ── Body ── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Left panel ── */}
        <Box sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderRight: '1px solid #e0e0e0', backgroundColor: '#fff', overflow: 'hidden' }}>

          <Box
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            sx={{ m: 1.5, p: 2, border: `2px dashed ${isDragging ? BRAND : '#ddd'}`,
              borderRadius: 2, textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
              backgroundColor: isDragging ? '#f5f0ff' : '#fafafa',
              '&:hover': { borderColor: BRAND, backgroundColor: '#f5f0ff' } }}>
            <CloudUploadIcon sx={{ fontSize: 28, color: isDragging ? BRAND : '#bbb', mb: 0.5 }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#555' }}>
              Drop PDF invoices or CSV exports
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: '#aaa', mt: 0.3 }}>
              Alfred reads PDFs · CSVs parse instantly
            </Typography>
            <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mt: 0.8 }}>
              <Chip icon={<PictureAsPdfIcon sx={{ fontSize: '11px !important' }} />} label="PDF" size="small" sx={{ fontSize: '0.56rem', height: 18 }} />
              <Chip icon={<TableChartIcon sx={{ fontSize: '11px !important' }} />} label="CSV" size="small" sx={{ fontSize: '0.56rem', height: 18 }} />
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <SupportedFormatsInfoButton />
            </Box>
          </Box>
          <input ref={fileInputRef} type="file" accept=".pdf,application/pdf,.csv,text/csv" multiple
            style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
            onChange={e => {
              const files = Array.from(e.target.files ?? []);
              const csvs = files.filter(f => f.name.toLowerCase().endsWith('.csv'));
              const pdfs = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
              if (csvs.length) handleCsvFile(csvs[0]);
              if (pdfs.length) {
                if (activeStatement) pdfs.forEach(f => addStatementPdfFile(f));
                else addPdfFiles(pdfs);
              }
              e.target.value = '';
            }} />

          {mode === 'bulk' && stats.pending + stats.errors > 0 && (
            <Box sx={{ px: 1.5, pb: 1 }}>
              <Button fullWidth variant="contained" size="small" startIcon={<BoltIcon />}
                onClick={bulkProcess} disabled={processing}
                sx={{ backgroundColor: BRAND, '&:hover': { backgroundColor: '#5a3e98' },
                  fontSize: '0.72rem', fontWeight: 700, py: 0.7 }}>
                {processing ? 'Processing…' : `Process ${stats.pending + stats.errors} PDF${stats.pending + stats.errors === 1 ? '' : 's'}`}
              </Button>
            </Box>
          )}

          {queue.length > 0 && (
            <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {stats.autoMatched > 0 && <Chip label={`${stats.autoMatched} auto-matched`} size="small" sx={{ fontSize: '0.58rem', backgroundColor: '#e8f5e9', color: '#2e7d32', height: 18 }} />}
              {stats.wip > 0         && <Chip label={`${stats.wip} WIP`}               size="small" sx={{ fontSize: '0.58rem', backgroundColor: '#fff3e0', color: '#e65100', height: 18 }} />}
              {stats.completed > 0   && <Chip label={`${stats.completed} done`}        size="small" sx={{ fontSize: '0.58rem', backgroundColor: '#dcedc8', color: '#1b5e20', height: 18 }} />}
              {stats.errors > 0      && <Chip label={`${stats.errors} errors`}         size="small" sx={{ fontSize: '0.58rem', backgroundColor: '#ffebee', color: '#b71c1c', height: 18 }} />}
            </Box>
          )}

          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filteredQueue.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', mt: 2 }}>
                <InsertDriveFileIcon sx={{ fontSize: 32, color: '#ddd', mb: 1 }} />
                <Typography sx={{ fontSize: '0.7rem', color: '#bbb' }}>
                  {queue.length === 0 ? 'No bills queued yet' : `No bills in "${filterTab}"`}
                </Typography>
              </Box>
            ) : (
              filteredQueue.map(item => {
                const cfg = STATUS_CFG[item.status];
                const isSelected = item.id === selectedId;
                const isCsv = item.source !== 'pdf';
                return (
                  <Box key={item.id} onClick={() => setSelectedId(item.id)}
                    sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                      backgroundColor: isSelected ? '#f5f0ff' : 'transparent',
                      borderLeft: isSelected ? `3px solid ${BRAND}` : '3px solid transparent',
                      opacity: item.status === 'skipped' ? 0.6 : 1,
                      '&:hover': { backgroundColor: isSelected ? '#f5f0ff' : '#fafafa' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      {isCsv
                        ? <TableChartIcon sx={{ fontSize: 18, color: '#558b2f', mt: 0.2, flexShrink: 0 }} />
                        : <PictureAsPdfIcon sx={{ fontSize: 18, color: '#e53935', mt: 0.2, flexShrink: 0 }} />}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.parsed?.supplierInvoiceNo || item.parsed?.supplierName || item.fileName.replace(/\.(pdf|csv)$/i, '')}
                        </Typography>
                        {item.parsed && (
                          <Typography sx={{ fontSize: '0.6rem', color: '#888', fontFamily: 'monospace',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {supplierName(item.supplierId) !== '—' ? supplierName(item.supplierId) : item.parsed.supplierName}
                            {' · '}
                            {fmt(item.parsed.totalIncGst)}
                            {item.parsed.docType === 'CRD' && ' (CRD)'}
                          </Typography>
                        )}
                        {item.status === 'processing' && item.stepMsg && (
                          <Typography sx={{ fontSize: '0.58rem', color: '#0277bd', mt: 0.3 }}>
                            {item.stepMsg}
                          </Typography>
                        )}
                        {item.status === 'processing' && <LinearProgress sx={{ mt: 0.5, height: 2 }} />}
                        {item.matchedPoId && (
                          <Typography sx={{ fontSize: '0.6rem', color: FI_ORANGE, fontFamily: 'monospace' }}>
                            → {item.matchedPoId}
                            {item.variancePct != null && Math.abs(item.variancePct) > 0.5 && (
                              <span style={{ color: Math.abs(item.variancePct) <= 2 ? '#2e7d32' : '#e65100', marginLeft: 4 }}>
                                ({item.variancePct > 0 ? '+' : ''}{item.variancePct.toFixed(1)}%)
                              </span>
                            )}
                          </Typography>
                        )}
                        {/* v1.14.4 — Soft suggestion when no confident match. Visually distinct
                            from a confirmed match (italic + muted) so users know it's a guess. */}
                        {!item.matchedPoId && item.suggestedPoId && (
                          <Typography sx={{ fontSize: '0.58rem', color: '#aaa', fontStyle: 'italic', fontFamily: 'monospace' }}>
                            ↪ Maybe {item.suggestedPoId}?
                          </Typography>
                        )}
                        {item.note && (
                          <Typography sx={{ fontSize: '0.56rem', color: '#aaa', fontStyle: 'italic', mt: 0.2 }}>
                            {item.note}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3,
                        px: 0.6, py: 0.2, borderRadius: 0.8, backgroundColor: cfg.bg, flexShrink: 0 }}>
                        <Box sx={{ color: cfg.color, display: 'flex', alignItems: 'center' }}>{cfg.icon}</Box>
                        <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: cfg.color, whiteSpace: 'nowrap' }}>
                          {cfg.label}
                        </Typography>
                      </Box>
                    </Box>

                    {mode === 'one-by-one' && item.status === 'pending' && item.source === 'pdf' && fileBuffer.current[item.id] && (
                      <Button size="small" variant="contained" startIcon={<Sparkle size={12} color="#fff" />}
                        onClick={e => { e.stopPropagation(); setSelectedId(item.id); parseFile(item.id, fileBuffer.current[item.id]); }}
                        sx={{ mt: 0.8, fontSize: '0.6rem', py: 0.3, backgroundColor: BRAND, '&:hover': { backgroundColor: '#4A0A82' }, textTransform: 'none' }}>
                        Parse this bill
                      </Button>
                    )}
                    {item.status === 'error' && item.source === 'pdf' && fileBuffer.current[item.id] && (
                      <Button size="small" variant="text" startIcon={<ReplayIcon />}
                        onClick={e => { e.stopPropagation(); parseFile(item.id, fileBuffer.current[item.id]); }}
                        sx={{ mt: 0.5, fontSize: '0.58rem', color: '#b71c1c' }}>
                        Retry
                      </Button>
                    )}
                  </Box>
                );
              })
            )}
          </Box>

          {queue.length > 0 && (
            <Box sx={{ p: 1, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 0.5 }}>
              <Button size="small" fullWidth variant="text" startIcon={<DeleteOutlineIcon />}
                onClick={() => { if (window.confirm('Clear completed & skipped items?')) setQueue(q => q.filter(i => i.status !== 'completed' && i.status !== 'skipped')); }}
                sx={{ fontSize: '0.62rem', color: '#888' }}>
                Clear done
              </Button>
              <Button size="small" fullWidth variant="text" startIcon={<DeleteOutlineIcon />}
                onClick={() => { if (window.confirm('Clear entire queue? This cannot be undone.')) { setQueue([]); fileBuffer.current = {}; setSelectedId(null); } }}
                sx={{ fontSize: '0.62rem', color: '#b71c1c' }}>
                Clear all
              </Button>
            </Box>
          )}
        </Box>

        {/* ── Right panel ── */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {filteredQueue.length === 0 ? <EmptyState activeStatement={activeStatement} /> : viewLevel === 'coarse' ? (
            <CoarseView
              queue={filteredQueue}
              purchaseOrders={purchaseOrders}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              applyBill={applyBill}
              startReview={startReview}
              markCompleted={markCompleted}
              markSkipped={markSkipped}
              reopenItem={reopenItem}
              removeItem={removeItem}
              manuallyLinkPo={manuallyLinkPo}
              openCreatePoDialog={openCreatePoDialog}
              openAssignPoDialog={setAssignPoFromId}
              openRejectDialog={setRejectFromId}
              updatePurchaseOrder={updatePurchaseOrder}
              parseFile={(id) => fileBuffer.current[id] && parseFile(id, fileBuffer.current[id])}
              mode={mode}
              navigate={navigate}
              supplierName={supplierName}
              stats={stats}
              bulkProcess={bulkProcess}
              processing={processing}
              onViewPo={setPoReviewId}
              totalQueueLength={queue.length}
              resetQueue={() => { setQueue([]); fileBuffer.current = {}; setSelectedId(null); }}
            />
          ) : (
            <DetailView
              selected={selected}
              applyBill={applyBill}
              startReview={startReview}
              markCompleted={markCompleted}
              markSkipped={markSkipped}
              navigate={navigate}
              supplierName={supplierName}
              onViewPo={setPoReviewId}
            />
          )}
        </Box>
      </Box>

      {/* ── CSV preview / supplier-assign modal ── */}
      {csvPreview && (
        <CsvPreviewDialog
          preview={csvPreview}
          setPreview={setCsvPreview}
          suppliers={suppliers}
          onConfirm={commitCsvImport}
        />
      )}

      {/* ── PO review / edit dialog ── */}
      {poReviewId && (
        <PoReviewDialog
          poId={poReviewId}
          onClose={() => setPoReviewId(null)}
          purchaseOrders={purchaseOrders}
          suppliers={suppliers}
          updatePurchaseOrder={updatePurchaseOrder}
          navigate={navigate}
        />
      )}

      {/* ── Create-PO-from-Invoice dialog ── */}
      {createPoFromId && (() => {
        const item = queue.find(q => q.id === createPoFromId);
        if (!item) return null;
        const lineCount = item.parsed?.lines?.length ?? 0;
        const total = item.parsed?.totalIncGst ?? 0;
        return (
          <Dialog open onClose={closeCreatePoDialog} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddIcon sx={{ color: '#6a1b9a' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>
                    Create PO from this Invoice
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>
                    Supplier billed us with no prior PO — capture the missing Job + Project, then we raise a draft PO with these line items.
                  </Typography>
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              {/* Invoice context */}
              <Box sx={{ p: 1.4, mb: 2, backgroundColor: '#f5f0ff', borderRadius: 1.5, border: `1px solid ${BRAND}22` }}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Supplier</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{item.parsed?.supplierName ?? '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Invoice #</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{item.parsed?.supplierInvoiceNo ?? '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Lines</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{lineCount}</Typography>
                  </Box>
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Inc GST</Typography>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: BRAND }}>{fmt(total)}</Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Job # — required */}
              <TextField fullWidth size="small" label="Job # *" placeholder="e.g. 24"
                value={newPoJob}
                onChange={e => setNewPoJob(e.target.value)}
                helperText={newPoJob.trim() ? `Will save on the new PO as JOB #${newPoJob.trim().replace(/[^0-9]/g, '')}` : 'Job number this work was performed for'}
                sx={{ mb: 2 }} />

              {/* Project ref — optional */}
              <TextField fullWidth size="small" label="Project (optional)" placeholder="e.g. PRJ-2026-04"
                value={newPoProject}
                onChange={e => setNewPoProject(e.target.value)}
                helperText="Only needed if this work belongs to a multi-job programme" />

              <Box sx={{ mt: 2, p: 1.2, backgroundColor: '#fff8e1', borderRadius: 1.5, border: '1px solid #ffe082', display: 'flex', gap: 1 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#f57c00', mt: 0.2 }} />
                <Typography sx={{ fontSize: '0.7rem', color: '#555', lineHeight: 1.55 }}>
                  We'll spawn a <strong>Draft PO</strong> with {lineCount} line item{lineCount !== 1 ? 's' : ''} taken straight from the invoice, then auto-link this invoice to it. You can fine-tune line prices on the PO afterwards before approving.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
              <Button onClick={closeCreatePoDialog} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!newPoJob.trim()}
                onClick={commitNewPoFromInvoice}
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: '#6a1b9a',
                  '&:hover': { backgroundColor: '#4a148c' } }}>
                Create draft PO &amp; link invoice
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* ── Assign PO dialog (v1.14.4) ─────────────────────────────────── */}
      {assignPoFromId && (() => {
        const item = queue.find(q => q.id === assignPoFromId);
        if (!item) return null;
        return (
          <AssignPoDialog
            item={item}
            purchaseOrders={purchaseOrders}
            onClose={() => setAssignPoFromId(null)}
            onAssign={(poId) => { manuallyLinkPo(item.id, poId); setAssignPoFromId(null); }}
            onCreateNew={() => { setAssignPoFromId(null); setCreatePoFromId(item.id); }}
          />
        );
      })()}

      {/* ── Reject dialog (v1.14.4) ────────────────────────────────────── */}
      {rejectFromId && (() => {
        const item = queue.find(q => q.id === rejectFromId);
        if (!item) return null;
        const close = () => { setRejectFromId(null); setRejectReason(''); };
        return (
          <Dialog open onClose={close} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pb: 1, color: '#b71c1c', fontWeight: 800, fontSize: '1rem' }}>
              Reject this supplier invoice?
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: '0.78rem', color: '#444', mb: 1.5 }}>
                Marks <strong>{item.parsed?.supplierInvoiceNo || item.fileName}</strong> as Rejected.
                The invoice stays in the queue under the <em>Rejected</em> tab for audit. Use this when
                the invoice is disputed, fraudulent, or simply wrong — not for simple duplicates (use Skip).
              </Typography>
              <TextField
                fullWidth size="small" multiline rows={2}
                label="Reason (optional)"
                placeholder="e.g. Qty doesn't match delivery docket; price disputed"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={close} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
              <Button variant="contained" color="error"
                onClick={() => { markRejected(item.id, rejectReason.trim() || undefined); close(); }}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Reject invoice
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────
interface EmptyStateProps {
  activeStatement: import('../data/types').Statement | null;
}

// Context-aware empty pane (v1.14.2). When scoped to a statement that has no
// queue items yet, we name the statement explicitly so the user understands
// why the list is empty and what dragging files in will do (auto-link to
// THIS statement, not the global stream).
const EmptyState: React.FC<EmptyStateProps> = ({ activeStatement }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    height: '100%', gap: 2, color: '#bbb', textAlign: 'center', px: 3 }}>
    <CloudUploadIcon sx={{ fontSize: 56 }} />
    <Box sx={{ textAlign: 'center', maxWidth: 520 }}>
      {activeStatement ? (
        <>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#666' }}>
            No invoices on this statement yet
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#888', mt: 0.6 }}>
            Drop the supplier statement file (PDF or CSV) into the dropzone on the left.
            Every invoice parsed from it auto-links to <strong style={{ color: '#555' }}>{activeStatement.filename}</strong>.
          </Typography>
          {activeStatement.invoiceCount > 0 && (
            <Typography sx={{ fontSize: '0.72rem', color: '#aaa', mt: 0.8, fontStyle: 'italic' }}>
              Note: this statement was seeded with {activeStatement.invoiceCount} invoice
              {activeStatement.invoiceCount !== 1 ? 's' : ''} / ${activeStatement.totalIncGst.toFixed(2)} as a demo summary.
              Drop the source file to parse and reconcile each line.
            </Typography>
          )}
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#ccc' }}>No invoices uploaded yet</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#ddd', mt: 0.5 }}>
            Drop PDF files or CSV exports into the left panel to get started
          </Typography>
        </>
      )}
    </Box>
    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
      <Paper sx={{ p: 2, width: 220, border: '1px solid #f0f0f0' }}>
        <PictureAsPdfIcon sx={{ color: '#e53935', mb: 0.5 }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#555' }}>PDF Invoices</Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#aaa', mt: 0.5 }}>
          Scanned or digital invoices — Alfred extracts line items and totals.
        </Typography>
      </Paper>
      <Paper sx={{ p: 2, width: 220, border: '1px solid #f0f0f0' }}>
        <TableChartIcon sx={{ color: '#558b2f', mb: 0.5 }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#555' }}>CSV Exports</Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#aaa', mt: 0.5 }}>
          Supplier portal exports (Selected-Invoices, SMARTBIZ) — one CSV → many bills, instant parse.
        </Typography>
      </Paper>
    </Stack>
  </Box>
);

// ── Reusable info dialog ─────────────────────────────────────────────────────
interface InfoDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}
const InfoDialog: React.FC<InfoDialogProps> = ({ open, onClose, title, subtitle, maxWidth = 'sm', children }) => (
  <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
    <DialogTitle sx={{ pb: subtitle ? 0.5 : 2, display: 'flex', alignItems: 'center', gap: 1 }}>
      <InfoOutlinedIcon sx={{ color: BRAND }} />
      <Box>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#333' }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>{subtitle}</Typography>}
      </Box>
    </DialogTitle>
    <DialogContent dividers sx={{ py: 2 }}>{children}</DialogContent>
    <DialogActions>
      <Button onClick={onClose} size="small" sx={{ textTransform: 'none', fontWeight: 600, color: BRAND }}>Got it</Button>
    </DialogActions>
  </Dialog>
);

// ── Small row renderer for action-info / format-info tables ─────────────────
const InfoRow: React.FC<{ label: string; badge?: string; badgeColor?: string; children: React.ReactNode }> = ({ label, badge, badgeColor = BRAND, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 1.5, py: 1,
    borderBottom: '1px dashed #eee', '&:last-of-type': { borderBottom: 'none' } }}>
    <Box>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#444' }}>{label}</Typography>
      {badge && (
        <Chip label={badge} size="small" sx={{ mt: 0.4, height: 16, fontSize: '0.55rem',
          backgroundColor: `${badgeColor}15`, color: badgeColor, fontWeight: 700 }} />
      )}
    </Box>
    <Box sx={{ fontSize: '0.72rem', color: '#555', lineHeight: 1.5 }}>{children}</Box>
  </Box>
);

// ── Actions info (i) ─────────────────────────────────────────────────────────
const ActionsInfoButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title="What do these actions do?" arrow placement="top">
        <IconButton size="small" onClick={e => { e.stopPropagation(); setOpen(true); }}
          sx={{ ml: 0.4, p: 0.2, color: '#999', '&:hover': { color: BRAND } }}>
          <InfoOutlinedIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
      <InfoDialog open={open} onClose={() => setOpen(false)} maxWidth="md"
        title="Row Actions — what each does"
        subtitle="Every supplier invoice moves through these actions as you reconcile it against a PO">
        <InfoRow label="Apply" badge="primary" badgeColor="#2e7d32">
          Creates a finance-ready <strong>Bill</strong>, links it to the matched PO, and consumes that PO value. Status → <em>Completed</em>.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> a $32.07 delivery that auto-matched to PO-1648 with 0% variance — one click posts it to finance.
          </Typography>
        </InfoRow>

        <InfoRow label="Apply All Auto-Matched" badge="bulk" badgeColor="#2e7d32">
          Applies <strong>every</strong> row that parsed cleanly with variance under the threshold, in one click. Skips anything flagged <em>Needs Review</em>.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> 8 of 16 invoices came in at exact PO price — bulk-apply those and focus your time on the 8 with variance.
          </Typography>
        </InfoRow>

        <InfoRow label="Review" badge="open detail">
          Opens the full <strong>Bill Detail & Sync</strong> page, where every invoice line is matched against PO lines. Edit qty/price, accept variance with a reason, then apply.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> a $1,924 invoice vs $661 PO — click Review to see which line items drove the variance before accepting.
          </Typography>
        </InfoRow>

        <InfoRow label="Link PO" badge="inline search">
          Shown on <em>No PO Found</em> rows. Inline searchable dropdown — pick the correct Purchase Order from the list and the row re-reconciles live.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> CSV came with no PO reference — type "1502" into the Matched PO cell and the variance recalculates instantly.
          </Typography>
        </InfoRow>

        <InfoRow label="Start Review" badge="manual">
          Moves a row into <em>WIP</em> so your team knows someone is working on it. Prevents two people applying the same bill.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> you pick up a disputed invoice at 4pm, mark it WIP, and resume it tomorrow morning.
          </Typography>
        </InfoRow>

        <InfoRow label="Mark Completed" badge="wip → done">
          Closes a WIP row once you've finished the manual review. Status → <em>Completed</em>.
        </InfoRow>

        <InfoRow label="Skip" badge="park">
          Parks the invoice without applying. Stays visible in the <em>Skipped</em> tab so nothing is silently lost.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> supplier sent a duplicate invoice — skip it, email them for a credit note, restore later if needed.
          </Typography>
        </InfoRow>

        <InfoRow label="Reopen" badge="undo">
          Pulls a Completed or Skipped row back into the live queue so it can be re-matched or re-applied.
        </InfoRow>

        <InfoRow label="Remove" badge="delete" badgeColor="#c62828">
          Deletes the row from the queue entirely (does not reverse any already-applied Bill — use the Bill Detail page for that).
        </InfoRow>

        <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f0ff', borderRadius: 1, border: '1px solid #e0d4ff' }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>
            <strong style={{ color: BRAND }}>Tip:</strong> actions only appear when they are valid for the row's current status.
            A <em>Completed</em> row shows only <em>View Bill</em> and <em>Reopen</em>; a <em>No PO Found</em> row shows <em>Link PO</em>, <em>Create PO</em>, and <em>Skip</em>.
          </Typography>
        </Box>
      </InfoDialog>
    </>
  );
};

// ── Supported formats / Support contact (i) ─────────────────────────────────
const SupportedFormatsInfoButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box onClick={e => { e.stopPropagation(); setOpen(true); }}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.6, cursor: 'pointer',
          color: '#999', fontSize: '0.6rem', '&:hover': { color: BRAND } }}>
        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
        <Typography sx={{ fontSize: '0.6rem', textDecoration: 'underline', textUnderlineOffset: 2 }}>
          Supported file types
        </Typography>
      </Box>
      <InfoDialog open={open} onClose={() => setOpen(false)} maxWidth="sm"
        title="Supported document types"
        subtitle="What the Bill Importer can process today — and how to request more">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#2e7d32', mb: 1 }}>
          ✓ Supported now
        </Typography>

        <InfoRow label="PDF invoice" badge="AI parse" badgeColor={BRAND}>
          Single supplier invoice, digital or scanned, up to 20 MB. Alfred parses with AI — takes ~5–10 seconds per file.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> "Kingy Steel - INV-187571.pdf"
          </Typography>
        </InfoRow>

        <InfoRow label="PDF multi-invoice" badge="batch parse" badgeColor={BRAND}>
          One PDF that contains several supplier invoices (e.g. consolidated supplier statement). Each invoice is extracted and queued separately.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> a 12-page statement from Kingy Steel covering a whole month of deliveries.
          </Typography>
        </InfoRow>

        <InfoRow label="CSV — Selected-Invoices" badge="instant" badgeColor="#2e7d32">
          Header-based export from supplier portals (e.g. Benash supplier download). Parsed client-side — no AI cost.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> "Selected-Invoices220469_2026-04-15.csv"
          </Typography>
        </InfoRow>

        <InfoRow label="CSV — SMARTBIZ" badge="instant" badgeColor="#2e7d32">
          Positional CSV from MYOB / SMARTBIZ exports. Supplier ABN auto-detected and remembered for next upload.<br/>
          <Typography component="span" sx={{ color: '#888', fontSize: '0.68rem' }}>
            <strong>Example:</strong> "SMARTBIZ Invoice Format (63).csv"
          </Typography>
        </InfoRow>

        <Box sx={{ mt: 2.5, mb: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#c62828', mb: 0.5 }}>
            ✗ Not supported yet
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#777', lineHeight: 1.5 }}>
            Excel (.xlsx/.xls), plain images (JPG/PNG), email attachments (.eml/.msg), EDI (X12/EDIFACT),
            JSON/XML feeds, QuickBooks / Xero direct exports, or any custom supplier format
            not listed above.
          </Typography>
        </Box>

        <Box sx={{ mt: 2, p: 1.8, backgroundColor: '#f5f0ff', borderRadius: 1.5, border: `1px solid ${BRAND}30` }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND, mb: 0.8 }}>
            Need a different format?
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#555', lineHeight: 1.55, mb: 1.2 }}>
            Contact <strong>FieldInsight Support</strong> to book a feature-request meeting or open an email thread.
            Please include:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: '0.68rem', color: '#555', '& li': { mb: 0.3 } }}>
            <li>A sample file (redact sensitive data)</li>
            <li>Supplier name and invoice volume per month</li>
            <li>Urgency and business impact</li>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button size="small" variant="contained" startIcon={<EmailIcon sx={{ fontSize: 14 }} />}
              href="mailto:support@fieldinsight.com.au?subject=Bill%20Importer%20-%20Feature%20request%20(new%20file%20format)&body=Hi%20FieldInsight%20Support%2C%0A%0AI%20would%20like%20to%20request%20support%20for%20a%20new%20document%20format%20in%20the%20Bill%20Importer.%0A%0ASupplier%20name%3A%20%0AFormat%2Ffile%20type%3A%20%0AInvoices%20per%20month%3A%20%0ABusiness%20impact%2Furgency%3A%20%0A%0A(Please%20attach%20a%20sample%20file.)%0A%0AThanks."
              sx={{ textTransform: 'none', fontSize: '0.68rem', fontWeight: 700, backgroundColor: BRAND, '&:hover': { backgroundColor: '#5a3e98' } }}>
              Email Support
            </Button>
            <Button size="small" variant="outlined" startIcon={<EventIcon sx={{ fontSize: 14 }} />}
              href="https://fieldinsight.com.au/support" target="_blank" rel="noopener"
              sx={{ textTransform: 'none', fontSize: '0.68rem', fontWeight: 700, borderColor: BRAND, color: BRAND }}>
              Book a Feature Meeting
            </Button>
          </Stack>
        </Box>
      </InfoDialog>
    </>
  );
};

// ── Coarse summary view ──────────────────────────────────────────────────────
interface CoarseProps {
  queue: QueueItem[];
  purchaseOrders: Array<{ id: string; reference?: string; jobRef?: string; total: number; status: string }>;
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  applyBill: (id: string) => void;
  startReview: (id: string) => void;
  markCompleted: (id: string) => void;
  markSkipped: (id: string) => void;
  reopenItem: (id: string) => void;
  removeItem: (id: string) => void;
  manuallyLinkPo: (id: string, poId: string | null) => void;
  openCreatePoDialog: (id: string) => void;
  openAssignPoDialog: (id: string) => void;
  openRejectDialog: (id: string) => void;
  updatePurchaseOrder: (id: string, updates: Partial<{ status: import('../data/types').PoStatus }>) => void;
  parseFile: (id: string) => void;
  mode: 'bulk' | 'one-by-one';
  navigate: (path: string) => void;
  supplierName: (id?: string) => string;
  stats: { autoMatched: number; pending: number };
  bulkProcess: () => void;
  processing: boolean;
  onViewPo: (poId: string) => void;
  resetQueue: () => void;
  totalQueueLength: number;
}

const CoarseView: React.FC<CoarseProps> = ({
  queue, purchaseOrders, selectedId, setSelectedId, applyBill, startReview, markCompleted, markSkipped,
  reopenItem, removeItem, manuallyLinkPo, openCreatePoDialog, openAssignPoDialog, openRejectDialog, parseFile, mode, navigate, supplierName, stats, bulkProcess, processing,
  onViewPo, resetQueue, totalQueueLength, updatePurchaseOrder,
}) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: BRAND }}>
        Bill Reconciliation
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', color: '#999' }}>
        {queue.length} invoice{queue.length !== 1 ? 's' : ''} ·{' '}
        {fmt(queue.reduce((s, q) => s + (q.parsed?.totalIncGst ?? 0), 0))} total
      </Typography>
      {mode === 'bulk' && stats.pending > 0 && (
        <Button variant="contained" size="small" startIcon={<BoltIcon />}
          onClick={bulkProcess} disabled={processing}
          sx={{ ml: 'auto', backgroundColor: BRAND, fontSize: '0.7rem' }}>
          {processing ? 'Processing…' : 'Process All Pending'}
        </Button>
      )}
      {stats.autoMatched > 0 && (
        <Button variant="contained" size="small" startIcon={<DoneAllIcon />}
          onClick={() => queue.filter(q => q.status === 'auto-matched').forEach(q => applyBill(q.id))}
          sx={{ backgroundColor: '#FF6A13', fontSize: '0.7rem', ml: stats.autoMatched > 0 && !(mode === 'bulk' && stats.pending > 0) ? 'auto' : 0 }}>
          Apply All Auto-Matched ({stats.autoMatched})
        </Button>
      )}
      {totalQueueLength > 0 && (
        <Tooltip arrow title={
          <Box sx={{ fontSize: '0.7rem' }}>
            <Box sx={{ fontWeight: 700, mb: 0.3 }}>Reset queue</Box>
            <Box>Clears every row in the importer queue (pending, in-progress, completed and skipped) and removes all parsed PDFs from memory.</Box>
            <Box sx={{ mt: 0.5, color: '#ffcdd2' }}>Use this to start fresh — e.g. after re-importing a CSV that was previously stacked.</Box>
          </Box>
        }>
          <Button variant="outlined" size="small" startIcon={<DeleteOutlineIcon />}
            onClick={() => {
              if (window.confirm(`Reset the entire queue? This will remove ALL ${totalQueueLength} item${totalQueueLength !== 1 ? 's' : ''} (pending, WIP, completed and skipped). This cannot be undone.`)) {
                resetQueue();
              }
            }}
            sx={{
              ml: stats.autoMatched > 0 || (mode === 'bulk' && stats.pending > 0) ? 0 : 'auto',
              fontSize: '0.7rem', color: '#b71c1c', borderColor: '#ef9a9a',
              '&:hover': { backgroundColor: '#ffebee', borderColor: '#b71c1c' },
            }}>
            Reset queue
          </Button>
        </Tooltip>
      )}
    </Box>

    <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
            <TableCell sx={{ width: 180, fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }}>File / Supplier</TableCell>
            <TableCell sx={{ width: 110, fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }}>Invoice #</TableCell>
            <TableCell sx={{ width: 90, fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }} align="right">Amount</TableCell>
            <TableCell sx={{ width: 110, fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }}>Matched PO</TableCell>
            <TableCell sx={{ width: 80, fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }} align="right">PO Value</TableCell>
            <TableCell sx={{ width: 130, fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }}>PO Status</TableCell>
            <TableCell sx={{ width: 60, fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }} align="right">Var %</TableCell>
            <TableCell sx={{ width: 120, fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.6rem', color: '#888', textTransform: 'uppercase' }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Actions
                <ActionsInfoButton />
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {queue.map(item => {
            const cfg = STATUS_CFG[item.status];
            const isSelected = item.id === selectedId;
            // v1.14 — surface "Bill Pending Goods" PO state with an amber
            // row tint so AP can spot IRNG rows at a glance. PO state takes
            // precedence over the queue-status background.
            const matchedPo = item.matchedPoId ? purchaseOrders.find(p => p.id === item.matchedPoId) : undefined;
            const isPendingGoods = matchedPo?.status === 'Bill received';
            return (
              <TableRow key={item.id} onClick={() => setSelectedId(item.id)}
                sx={{ cursor: 'pointer',
                  backgroundColor: isSelected ? '#f5f0ff' :
                    isPendingGoods                 ? '#fff8e1' :
                    item.status === 'needs-review' ? '#fff8f0' :
                    item.status === 'no-po'        ? '#fdf4ff' :
                    item.status === 'wip'          ? '#fff8e1' :
                    item.status === 'completed'    ? '#f1f8e9' :
                    item.status === 'skipped'      ? '#fafafa' : undefined,
                  opacity: item.status === 'skipped' ? 0.65 : 1,
                  '&:hover': { backgroundColor: isSelected ? '#f5f0ff' : '#fafafa' } }}>
                <TableCell sx={{ overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {item.source !== 'pdf'
                      ? <TableChartIcon sx={{ fontSize: 12, color: '#558b2f' }} />
                      : <PictureAsPdfIcon sx={{ fontSize: 12, color: '#e53935' }} />}
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {supplierName(item.supplierId) !== '—' ? supplierName(item.supplierId) : item.parsed?.supplierName || item.fileName.replace(/\.(pdf|csv)$/i, '')}
                    </Typography>
                  </Box>
                  {item.status === 'processing' && <LinearProgress sx={{ mt: 0.4, height: 2 }} />}
                </TableCell>
                <TableCell sx={{ overflow: 'hidden' }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#555', fontFamily: 'monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.parsed?.supplierInvoiceNo || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600,
                    color: item.parsed?.docType === 'CRD' ? '#b71c1c' : '#333' }}>
                    {item.parsed ? fmt(item.parsed.totalIncGst) : '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ overflow: 'visible', p: item.status === 'wip' ? 0.3 : undefined }}
                  onClick={e => e.stopPropagation()}>
                  {/*
                    v1.14.4 — PO column rendering rules
                      • status='wip'      → inline Autocomplete (power-user fast path)
                      • status='no-po'    → BLANK (with optional "Did you mean…" chip).
                                             The action cell shows [Assign PO][Create PO].
                      • matchedPoId set   → clickable PO id (unchanged).
                      • parsed.customerPoRef present but no match → grey hint with "?"
                  */}
                  {item.status === 'wip' && item.parsed ? (
                    <Autocomplete size="small"
                      options={purchaseOrders}
                      value={purchaseOrders.find(p => p.id === item.matchedPoId) ?? null}
                      getOptionLabel={p => p.id}
                      onChange={(_e, val) => manuallyLinkPo(item.id, val?.id ?? null)}
                      sx={{ minWidth: 130 }}
                      renderOption={(p, po) => (
                        <li {...p} key={po.id}>
                          <Box>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: FI_ORANGE, fontFamily: 'monospace' }}>{po.id}</Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: '#888' }}>
                              {po.reference || '—'} · {po.jobRef || '—'} · {fmt(po.total)}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={params => (
                        <TextField {...params} variant="outlined" size="small"
                          placeholder={item.parsed?.customerPoRef ? `${item.parsed.customerPoRef}?` : 'Link PO…'}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.65rem', fontFamily: 'monospace', py: 0.4 } }} />
                      )} />
                  ) : item.status === 'no-po' ? (
                    item.suggestedPoId ? (
                      <Tooltip title={`Suggested by matcher: ${item.suggestedPoReason || 'supplier-name guess'} — click Assign PO to confirm or pick another`}>
                        <Chip
                          size="small"
                          icon={<HelpOutlineIcon sx={{ fontSize: '11px !important' }} />}
                          label={`Maybe ${item.suggestedPoId}?`}
                          sx={{ fontSize: '0.6rem', backgroundColor: '#fff8e1', color: '#5d4037',
                            border: '1px solid #ffe082', fontFamily: 'monospace', height: 20 }} />
                      </Tooltip>
                    ) : (
                      <Typography sx={{ fontSize: '0.62rem', color: '#aaa', fontStyle: 'italic' }}>
                        Not matched
                      </Typography>
                    )
                  ) : item.matchedPoId ? (
                    <Tooltip title={`Review & edit ${item.matchedPoId}`}>
                      <Typography
                        onClick={() => onViewPo(item.matchedPoId!)}
                        sx={{ fontSize: '0.65rem', fontWeight: 700, color: FI_ORANGE,
                          fontFamily: 'monospace', whiteSpace: 'nowrap', cursor: 'pointer',
                          textDecoration: 'underline', textDecorationColor: 'transparent',
                          transition: 'all 0.15s',
                          '&:hover': { color: '#e65100', textDecorationColor: '#e65100' } }}>
                        {item.matchedPoId}
                      </Typography>
                    </Tooltip>
                  ) : item.parsed?.customerPoRef ? (
                    <Typography sx={{ fontSize: '0.62rem', color: '#888', fontFamily: 'monospace', fontStyle: 'italic' }}>
                      {item.parsed.customerPoRef}?
                    </Typography>
                  ) : <Typography sx={{ fontSize: '0.65rem', color: '#ccc' }}>—</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontSize: '0.65rem', color: '#555' }}>
                    {item.matchedPoTotal != null ? fmt(item.matchedPoTotal) : '—'}
                  </Typography>
                </TableCell>
                {/*
                  PO Status — editable dropdown (v1.10.5).
                  When the row has a matched PO, render a small Select that
                  writes back to the global PO record via updatePurchaseOrder.
                  Without a matched PO it falls back to "—".
                */}
                <TableCell onClick={e => e.stopPropagation()} sx={{ p: 0.3 }}>
                  {item.matchedPoId ? (() => {
                    const matchedPo = purchaseOrders.find(p => p.id === item.matchedPoId);
                    return matchedPo ? (
                      <Select
                        size="small"
                        value={matchedPo.status}
                        onChange={e =>
                          updatePurchaseOrder(matchedPo.id, { status: e.target.value as import('../data/types').PoStatus })
                        }
                        sx={{ width: '100%',
                          '& .MuiSelect-select': { fontSize: '0.62rem', py: 0.4, px: 0.6, fontWeight: 700,
                            color: '#333' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0d5f5' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND } }}
                      >
                        {PO_STATUS_OPTIONS.map(s => (
                          <MenuItem key={s} value={s} sx={{ fontSize: '0.7rem' }}>{s}</MenuItem>
                        ))}
                      </Select>
                    ) : <Typography sx={{ fontSize: '0.65rem', color: '#ccc' }}>—</Typography>;
                  })() : <Typography sx={{ fontSize: '0.65rem', color: '#ccc' }}>—</Typography>}
                </TableCell>
                <TableCell align="right">
                  {item.variancePct != null ? (
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600,
                      color: Math.abs(item.variancePct) <= 2 ? '#2e7d32' : '#e65100' }}>
                      {item.variancePct > 0 ? '+' : ''}{item.variancePct.toFixed(1)}%
                    </Typography>
                  ) : <Typography sx={{ fontSize: '0.65rem', color: '#ccc' }}>—</Typography>}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4,
                    px: 0.6, py: 0.15, borderRadius: 1, backgroundColor: cfg.bg }}>
                    <Box sx={{ color: cfg.color, display: 'flex', alignItems: 'center' }}>{cfg.icon}</Box>
                    <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, color: cfg.color, whiteSpace: 'nowrap' }}>
                      {cfg.label}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                  <Stack direction="row" spacing={0.4} flexWrap="wrap">
                    {/* v1.14.4 — Auto-matched: single Approve action (variance is fine) */}
                    {item.status === 'auto-matched' && (
                      <Tooltip title="Approve the match — creates the bill and syncs values to the PO">
                        <Button size="small" variant="contained" onClick={() => applyBill(item.id)}
                          sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, backgroundColor: '#FF6A13',
                            '&:hover': { backgroundColor: '#F35627' }, whiteSpace: 'nowrap' }}>
                          Approve
                        </Button>
                      </Tooltip>
                    )}
                    {/* v1.14.4 — Needs-review: 3 explicit outcomes */}
                    {item.status === 'needs-review' && (
                      <>
                        <Tooltip title="Approve as-is — the variance is acceptable (e.g. partial delivery already known)">
                          <Button size="small" variant="contained" onClick={() => applyBill(item.id)}
                            sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, backgroundColor: '#FF6A13',
                              '&:hover': { backgroundColor: '#F35627' }, whiteSpace: 'nowrap' }}>
                            Approve
                          </Button>
                        </Tooltip>
                        <Tooltip title="Adjust the PO — open the bill detail to line-edit and sync the invoice values to the PO">
                          <Button size="small" variant="outlined" startIcon={<PlayArrowIcon sx={{ fontSize: '10px !important' }} />}
                            onClick={() => startReview(item.id)}
                            sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, color: '#e65100', borderColor: '#e65100' }}>
                            Adjust PO
                          </Button>
                        </Tooltip>
                        <Tooltip title="Reject — invoice is disputed / wrong (asks for reason)">
                          <Button size="small" variant="outlined" startIcon={<BlockIcon sx={{ fontSize: '10px !important' }} />}
                            onClick={() => openRejectDialog(item.id)}
                            sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, color: '#b71c1c', borderColor: '#b71c1c' }}>
                            Reject
                          </Button>
                        </Tooltip>
                      </>
                    )}
                    {/* WIP: Mark complete / open detail */}
                    {item.status === 'wip' && (
                      <>
                        <Tooltip title="Open full Bill Detail & Sync page">
                          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: '10px !important' }} />}
                            onClick={() => item.billId && navigate(`/bills/${item.billId}`)}
                            sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, color: BRAND, borderColor: BRAND }}>
                            Open
                          </Button>
                        </Tooltip>
                        <Tooltip title="Mark reconciliation as complete">
                          <Button size="small" variant="contained" startIcon={<DoneAllIcon sx={{ fontSize: '10px !important' }} />}
                            onClick={() => markCompleted(item.id)}
                            sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, backgroundColor: '#F35627' }}>
                            Done
                          </Button>
                        </Tooltip>
                      </>
                    )}
                    {/* Completed: open / reopen */}
                    {item.status === 'completed' && item.billId && (
                      <Tooltip title="Open bill">
                        <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: '10px !important' }} />}
                          onClick={() => navigate(`/bills/${item.billId}`)}
                          sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, color: BRAND, borderColor: BRAND }}>
                          Detail
                        </Button>
                      </Tooltip>
                    )}
                    {/* v1.14.4 — No PO: 4 explicit assignment options in one dialog */}
                    {item.status === 'no-po' && (
                      <>
                        <Tooltip title="Open Assign PO — pick by PO #, Job # or Project (and the matcher's best guess if any)">
                          <Button size="small" variant="contained" startIcon={<HelpOutlineIcon sx={{ fontSize: '10px !important' }} />}
                            onClick={() => openAssignPoDialog(item.id)}
                            sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, whiteSpace: 'nowrap',
                              backgroundColor: '#6a1b9a',
                              '&:hover': { backgroundColor: '#4a148c' } }}>
                            Assign PO
                          </Button>
                        </Tooltip>
                        <Tooltip title="Create a brand-new PO from this invoice (asks Job + Project)">
                          <Button size="small" variant="outlined" startIcon={<AddIcon sx={{ fontSize: '10px !important' }} />}
                            onClick={() => openCreatePoDialog(item.id)}
                            sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7, whiteSpace: 'nowrap',
                              color: '#6a1b9a', borderColor: '#6a1b9a' }}>
                            Create PO
                          </Button>
                        </Tooltip>
                      </>
                    )}
                    {/* Pending PDF */}
                    {item.status === 'pending' && mode === 'one-by-one' && item.source === 'pdf' && (
                      <Button size="small" variant="outlined"
                        onClick={() => parseFile(item.id)}
                        sx={{ fontSize: '0.58rem', py: 0.2, color: BRAND, borderColor: BRAND, whiteSpace: 'nowrap' }}>
                        Parse
                      </Button>
                    )}
                    {/* Error */}
                    {item.status === 'error' && (
                      <Button size="small" variant="text" startIcon={<ReplayIcon sx={{ fontSize: '10px !important' }} />}
                        onClick={() => parseFile(item.id)}
                        sx={{ fontSize: '0.58rem', py: 0.2, color: '#b71c1c' }}>
                        Retry
                      </Button>
                    )}
                    {/* Skip (always available except when completed) */}
                    {item.status !== 'completed' && item.status !== 'skipped' && (
                      <Tooltip title="Skip / park for later">
                        <IconButton size="small" onClick={() => markSkipped(item.id)}
                          sx={{ p: 0.3 }}>
                          <BlockIcon sx={{ fontSize: 13, color: '#aaa' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Reopen for skipped/completed — prominent button so users can rescue a row */}
                    {(item.status === 'skipped' || item.status === 'completed') && (
                      <Tooltip title={item.status === 'skipped'
                        ? 'Reopen — pull this row back into the live queue (e.g. recover a duplicate-flagged row)'
                        : 'Reopen completed bill — re-add to the queue for re-matching'}>
                        <Button size="small" variant="outlined"
                          startIcon={<ReplayIcon sx={{ fontSize: '11px !important' }} />}
                          onClick={() => reopenItem(item.id)}
                          sx={{ fontSize: '0.58rem', py: 0.2, px: 0.7,
                            color: item.status === 'skipped' ? '#0277bd' : '#2e7d32',
                            borderColor: item.status === 'skipped' ? '#0277bd' : '#2e7d32',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              backgroundColor: item.status === 'skipped' ? '#e3f2fd' : '#e8f5e9',
                              borderColor: item.status === 'skipped' ? '#01579b' : '#1b5e20',
                            },
                          }}>
                          Reopen
                        </Button>
                      </Tooltip>
                    )}
                    {/* Remove */}
                    <Tooltip title="Remove from queue">
                      <IconButton size="small" onClick={() => removeItem(item.id)}
                        sx={{ p: 0.3 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 13, color: '#ccc' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  </Box>
);

// ── Detail view ───────────────────────────────────────────────────────────────
interface DetailProps {
  selected: QueueItem | null;
  applyBill: (id: string) => void;
  startReview: (id: string) => void;
  markCompleted: (id: string) => void;
  markSkipped: (id: string) => void;
  navigate: (path: string) => void;
  supplierName: (id?: string) => string;
  onViewPo: (poId: string) => void;
}

const DetailView: React.FC<DetailProps> = ({ selected, applyBill, startReview, markCompleted, markSkipped, navigate, supplierName, onViewPo }) => {
  if (!selected || !selected.parsed) {
    return (
      <Alert severity="info" sx={{ maxWidth: 500 }}>
        Select a parsed bill from the queue on the left to see its line-item detail.
      </Alert>
    );
  }
  const cfg = STATUS_CFG[selected.status];
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: BRAND }}>
          {supplierName(selected.supplierId) !== '—' ? supplierName(selected.supplierId) : selected.parsed.supplierName || 'Unknown Supplier'}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#888', fontFamily: 'monospace' }}>
          {selected.parsed.supplierInvoiceNo}
        </Typography>
        <Chip label={fmt(selected.parsed.totalIncGst)} size="small" sx={{ fontSize: '0.68rem', fontWeight: 700 }} />
        {selected.parsed.docType === 'CRD' && (
          <Chip label="Credit Note" size="small" sx={{ fontSize: '0.65rem', backgroundColor: '#ffebee', color: '#b71c1c' }} />
        )}
        {selected.matchedPoId && (
          <Tooltip title={`Review & edit ${selected.matchedPoId}`}>
            <Chip label={`→ ${selected.matchedPoId}`} size="small"
              onClick={() => onViewPo(selected.matchedPoId!)}
              sx={{ fontSize: '0.68rem', backgroundColor: '#fff3e0', color: FI_ORANGE, fontWeight: 700,
                cursor: 'pointer', '&:hover': { backgroundColor: '#ffe0b2' } }} />
          </Tooltip>
        )}
        <Chip icon={cfg.icon as React.ReactElement} label={cfg.label} size="small"
          sx={{ fontSize: '0.65rem', backgroundColor: cfg.bg, color: cfg.color, fontWeight: 700,
            '& .MuiChip-icon': { color: cfg.color } }} />
        <Box sx={{ flex: 1 }} />
        {selected.status === 'auto-matched' || selected.status === 'needs-review' ? (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" onClick={() => startReview(selected.id)}
              sx={{ fontSize: '0.7rem', color: '#e65100', borderColor: '#e65100' }}>
              Start Review (WIP)
            </Button>
            <Button variant="contained" size="small" onClick={() => applyBill(selected.id)}
              sx={{ backgroundColor: '#FF6A13', fontSize: '0.7rem' }}>
              Apply to PO
            </Button>
          </Stack>
        ) : selected.status === 'wip' ? (
          <Stack direction="row" spacing={1}>
            {selected.billId && (
              <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />}
                onClick={() => navigate(`/bills/${selected.billId}`)}
                sx={{ fontSize: '0.7rem', color: BRAND, borderColor: BRAND }}>
                Open Bill Detail & Sync
              </Button>
            )}
            <Button variant="contained" size="small" startIcon={<DoneAllIcon />}
              onClick={() => markCompleted(selected.id)}
              sx={{ backgroundColor: '#F35627', fontSize: '0.7rem' }}>
              Mark Completed
            </Button>
          </Stack>
        ) : selected.status === 'completed' && selected.billId ? (
          <Button variant="contained" size="small" startIcon={<OpenInNewIcon />}
            onClick={() => navigate(`/bills/${selected.billId}`)}
            sx={{ backgroundColor: BRAND, fontSize: '0.7rem' }}>
            Open Bill
          </Button>
        ) : null}
        {selected.status !== 'completed' && selected.status !== 'skipped' && (
          <Button variant="text" size="small" startIcon={<BlockIcon />}
            onClick={() => markSkipped(selected.id)}
            sx={{ fontSize: '0.65rem', color: '#888', ml: 0.5 }}>
            Skip
          </Button>
        )}
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 1.5, flex: 1, border: '1px solid #e0e0e0' }}>
          <Typography sx={{ fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase', fontWeight: 700, mb: 0.5 }}>Invoice Info</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            {[
              ['Invoice No.',    selected.parsed.supplierInvoiceNo],
              ['Date',           selected.parsed.invoiceDate || '—'],
              ['Subtotal ex-GST', fmt(selected.parsed.subtotalExGst)],
              ['GST',            fmt(selected.parsed.totalGst)],
              ['Total inc-GST',  fmt(selected.parsed.totalIncGst)],
              ['Customer PO Ref', selected.parsed.customerPoRef || '—'],
            ].map(([label, val]) => (
              <Box key={label}>
                <Typography sx={{ fontSize: '0.6rem', color: '#aaa' }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{val}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {selected.matchedPoId && (
          <Paper sx={{ p: 1.5, flex: 1, border: '1px solid #e0e0e0',
            backgroundColor: selected.status === 'auto-matched' ? '#f1f8e9' : '#fff8f0' }}>
            <Typography sx={{ fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase', fontWeight: 700, mb: 0.5 }}>Matched PO</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
              {[
                ['FI PO ID',    selected.matchedPoId],
                ['PO Value',    fmt(selected.matchedPoTotal ?? 0)],
                ['Bill Amount', fmt(selected.parsed.totalIncGst)],
                ['Variance',    selected.variance != null ? fmt(selected.variance) : '—'],
                ['Variance %',  selected.variancePct != null ? `${selected.variancePct.toFixed(1)}%` : '—'],
                ['Match status', STATUS_CFG[selected.status].label],
              ].map(([label, val]) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#aaa' }}>{label}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{val}</Typography>
                </Box>
              ))}
            </Box>
            <Stack direction="row" spacing={0.8} sx={{ mt: 1 }}>
              <Button size="small" variant="contained" fullWidth
                onClick={() => onViewPo(selected.matchedPoId!)}
                sx={{ fontSize: '0.65rem', backgroundColor: FI_ORANGE,
                  '&:hover': { backgroundColor: '#e65100' } }}>
                Review PO
              </Button>
              <Button size="small" variant="outlined" fullWidth startIcon={<OpenInNewIcon />}
                onClick={() => navigate(`/po-print/${selected.matchedPoId}`)}
                sx={{ fontSize: '0.65rem', color: FI_ORANGE, borderColor: FI_ORANGE }}>
                Print
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>

      <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 0.8, backgroundColor: '#f5f0ff', borderBottom: '1px solid #e8e0f5' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: BRAND }}>
            Parsed Line Items — {selected.parsed.lines.length} line{selected.parsed.lines.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Code</TableCell>
              <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Description</TableCell>
              <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }} align="right">Qty</TableCell>
              <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }} align="right">Unit</TableCell>
              <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }} align="right">Total ex-GST</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selected.parsed.lines.map((line, i) => (
              <TableRow key={i} sx={{ '&:nth-of-type(even)': { backgroundColor: '#fafafa' } }}>
                <TableCell>
                  <Typography sx={{ fontSize: '0.67rem', fontFamily: 'monospace', color: '#555' }}>
                    {line.supplierCode}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: '0.67rem', color: '#333' }}>
                    {line.description}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontSize: '0.67rem', color: '#555' }}>{line.qty}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontSize: '0.67rem', color: '#555' }}>{fmt(line.unitPrice)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#333' }}>
                    {fmt(line.lineTotal)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ px: 2, py: 1, backgroundColor: '#f5f5f5', display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
          <Typography sx={{ fontSize: '0.67rem', color: '#888' }}>Subtotal ex-GST: <strong>{fmt(selected.parsed.subtotalExGst)}</strong></Typography>
          <Typography sx={{ fontSize: '0.67rem', color: '#888' }}>GST: <strong>{fmt(selected.parsed.totalGst)}</strong></Typography>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#333' }}>Total inc-GST: {fmt(selected.parsed.totalIncGst)}</Typography>
        </Box>
      </Paper>

      {selected.status === 'completed' && selected.billId && (
        <Alert severity="success" sx={{ mt: 2 }} action={
          <Button size="small" onClick={() => navigate(`/bills/${selected.billId}`)}>
            Open Bill Detail &amp; Sync →
          </Button>
        }>
          Bill created and linked to {selected.matchedPoId}. Open the Bill Detail page for full line-item sync.
        </Alert>
      )}
    </Box>
  );
};

// ── CSV preview / supplier-assign dialog ─────────────────────────────────────
interface Supplier { id: string; name: string; abn?: string }
interface CsvPreviewDialogProps {
  preview: { fileName: string; detection: CsvDetectionResult; supplierId: string };
  setPreview: (p: { fileName: string; detection: CsvDetectionResult; supplierId: string } | null) => void;
  suppliers: Supplier[];
  onConfirm: () => void;
}

const CsvPreviewDialog: React.FC<CsvPreviewDialogProps> = ({ preview, setPreview, suppliers, onConfirm }) => {
  const { detection, fileName } = preview;
  const formatName = formatLabel(detection.format);
  const invoicesPreview = detection.invoices.slice(0, 5);
  const moreCount = detection.invoices.length - invoicesPreview.length;

  return (
    <Dialog open={true} onClose={() => setPreview(null)} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TableChartIcon sx={{ color: '#558b2f' }} />
        CSV Invoice Import
      </DialogTitle>
      <DialogContent>
        {/* Detection banner */}
        <Alert severity={detection.format === 'unknown' ? 'error' : 'success'}
          sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                Detected: {formatName}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#555', mt: 0.3 }}>
                <strong>{detection.totalInvoices}</strong> invoices ·{' '}
                <strong>{detection.totalLines}</strong> lines · total{' '}
                <strong>{fmt(detection.totalValue)}</strong>
                {detection.dateRange.from && detection.dateRange.to && (
                  <> · {detection.dateRange.from} → {detection.dateRange.to}</>
                )}
              </Typography>
              {detection.supplierAbn && (
                <Typography sx={{ fontSize: '0.65rem', color: '#888', mt: 0.3 }}>
                  Supplier ABN detected: <code>{detection.supplierAbn}</code>
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.62rem', color: '#aaa', mt: 0.3 }}>
                File: {fileName}
              </Typography>
            </Box>
          </Box>
        </Alert>

        {/* Supplier assignment */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#666', mb: 0.8, fontWeight: 700, textTransform: 'uppercase' }}>
            Which supplier is this file from?
          </Typography>
          <Autocomplete
            size="small"
            options={suppliers}
            getOptionLabel={s => s.name}
            value={suppliers.find(s => s.id === preview.supplierId) ?? null}
            onChange={(_e, val) => setPreview({ ...preview, supplierId: val?.id ?? '' })}
            renderInput={params => (
              <TextField {...params}
                placeholder="Select supplier…"
                helperText={preview.supplierId ? undefined : 'Required — each bill will be attributed to this supplier'}
                error={!preview.supplierId} />
            )}
            renderOption={(p, s) => (
              <li {...p} key={s.id}>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{s.name}</Typography>
                  {s.abn && <Typography sx={{ fontSize: '0.65rem', color: '#888' }}>ABN: {s.abn}</Typography>}
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Invoice preview table */}
        <Typography sx={{ fontSize: '0.68rem', color: '#666', mb: 0.8, fontWeight: 700, textTransform: 'uppercase' }}>
          Preview — first {invoicesPreview.length} of {detection.totalInvoices} invoices
        </Typography>
        <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Invoice #</TableCell>
                <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Type</TableCell>
                <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Date</TableCell>
                <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Customer PO</TableCell>
                <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }} align="right">Lines</TableCell>
                <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoicesPreview.map(inv => (
                <TableRow key={inv.supplierInvoiceNo}>
                  <TableCell sx={{ fontSize: '0.68rem', fontFamily: 'monospace' }}>{inv.supplierInvoiceNo}</TableCell>
                  <TableCell>
                    <Chip label={inv.docType} size="small"
                      sx={{ fontSize: '0.58rem', height: 18,
                        backgroundColor: inv.docType === 'CRD' ? '#ffebee' : '#e8f5e9',
                        color: inv.docType === 'CRD' ? '#b71c1c' : '#2e7d32' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.68rem' }}>{inv.invoiceDate || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.68rem', fontFamily: 'monospace', color: '#555' }}>{inv.customerPoRef || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.68rem' }}>{inv.lines.length}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 600,
                    color: inv.docType === 'CRD' ? '#b71c1c' : '#333' }}>
                    {fmt(inv.totalIncGst)}
                  </TableCell>
                </TableRow>
              ))}
              {moreCount > 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', color: '#aaa', fontSize: '0.65rem', fontStyle: 'italic' }}>
                    + {moreCount} more invoice{moreCount !== 1 ? 's' : ''}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        {detection.errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Parse warnings</Typography>
            {detection.errors.map((e, i) => (
              <Typography key={i} sx={{ fontSize: '0.65rem' }}>• {e}</Typography>
            ))}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button variant="outlined" size="small" onClick={() => setPreview(null)}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small"
          disabled={!preview.supplierId || detection.totalInvoices === 0}
          onClick={onConfirm}
          sx={{ backgroundColor: BRAND, fontWeight: 700 }}>
          Import {detection.totalInvoices} invoice{detection.totalInvoices !== 1 ? 's' : ''} as bills
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── PO review / edit dialog ──────────────────────────────────────────────────
// Opens when the user clicks a "Matched PO" cell in the Bill Reconciliation list.
// Shows all PO fields, lets them edit the header (date, delivery, jobRef, reference,
// notes, status), and has a BIG "Back to Bill List" button for an easy return.
const PO_STATUS_OPTIONS: Array<import('../data/types').PoStatus> = [
  'Draft', 'Submitted', 'Approved', 'Partial received',
  'All received', 'Bill received', 'Finalised', 'Cancelled',
];

interface PoReviewDialogProps {
  poId: string;
  onClose: () => void;
  purchaseOrders: import('../data/types').PurchaseOrder[];
  suppliers: Array<{ id: string; name: string; abn?: string; code?: string }>;
  updatePurchaseOrder: (poId: string, updates: Partial<import('../data/types').PurchaseOrder>) => void;
  navigate: (path: string) => void;
}

const PoReviewDialog: React.FC<PoReviewDialogProps> = ({
  poId, onClose, purchaseOrders, suppliers, updatePurchaseOrder, navigate,
}) => {
  const po = purchaseOrders.find(p => p.id === poId);
  const supplier = po ? suppliers.find(s => s.id === po.supplierId) : undefined;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<import('../data/types').PurchaseOrder>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (po) {
      setDraft({
        date: po.date,
        deliveryDate: po.deliveryDate ?? '',
        jobRef: po.jobRef,
        reference: po.reference,
        notes: po.notes ?? '',
        status: po.status,
      });
      setEditing(false);
      setSaved(false);
    }
  }, [poId, po]);

  if (!po) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#b71c1c' }}>PO not found</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Couldn't find <strong>{poId}</strong> in the current PO list.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" size="large" startIcon={<ArrowBackIcon />} onClick={onClose}
            sx={{ backgroundColor: BRAND, fontWeight: 700, px: 3, py: 1 }}>
            Back to Bill List
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const handleSave = () => {
    updatePurchaseOrder(poId, {
      date: draft.date,
      deliveryDate: draft.deliveryDate || undefined,
      jobRef: draft.jobRef ?? '',
      reference: draft.reference ?? '',
      notes: draft.notes ?? '',
      status: draft.status,
    });
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleCancelEdit = () => {
    setDraft({
      date: po.date,
      deliveryDate: po.deliveryDate ?? '',
      jobRef: po.jobRef,
      reference: po.reference,
      notes: po.notes ?? '',
      status: po.status,
    });
    setEditing(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      {/* Top bar with a prominent Back button */}
      <Box sx={{ px: 2, py: 1.2, backgroundColor: '#f5f0ff', borderBottom: `1px solid ${BRAND}33`,
        display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Button variant="contained" size="medium" startIcon={<ArrowBackIcon />} onClick={onClose}
          sx={{ backgroundColor: BRAND, fontWeight: 700, textTransform: 'none', px: 2,
            '&:hover': { backgroundColor: '#4a3580' } }}>
          Back to Bill List
        </Button>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: BRAND, lineHeight: 1.2 }}>
            {po.id} · {supplier?.name ?? 'Unknown supplier'}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>
            Review & edit this Purchase Order
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <StatusChip status={draft.status ?? po.status} sx={{ fontSize: '0.7rem' }} />
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        {saved && (
          <Alert severity="success" sx={{ mb: 2 }}>Saved — changes applied to {po.id}.</Alert>
        )}

        {/* Summary row */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Paper sx={{ flex: 1, p: 1.5, border: `1px solid ${FI_ORANGE}30`, backgroundColor: '#fff8f0' }}>
            <Typography sx={{ fontSize: '0.58rem', color: '#aaa', textTransform: 'uppercase', fontWeight: 700 }}>
              Total (inc GST)
            </Typography>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: FI_ORANGE }}>
              {fmt(po.total)}
            </Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 1.5, border: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontSize: '0.58rem', color: '#aaa', textTransform: 'uppercase', fontWeight: 700 }}>
              Ex GST
            </Typography>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#333' }}>
              {fmt(po.exGst)}
            </Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 1.5, border: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontSize: '0.58rem', color: '#aaa', textTransform: 'uppercase', fontWeight: 700 }}>
              GST
            </Typography>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#333' }}>
              {fmt(po.gst)}
            </Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 1.5, border: '1px solid #e0e0e0' }}>
            <Typography sx={{ fontSize: '0.58rem', color: '#aaa', textTransform: 'uppercase', fontWeight: 700 }}>
              Lines
            </Typography>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#333' }}>
              {po.lines}
            </Typography>
          </Paper>
        </Stack>

        {/* PO fields — editable block */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#666', fontWeight: 700, textTransform: 'uppercase' }}>
            Purchase Order Details
          </Typography>
          {!editing ? (
            <Button size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: '13px !important' }} />}
              onClick={() => setEditing(true)}
              sx={{ fontSize: '0.7rem', color: BRAND, borderColor: BRAND }}>
              Edit
            </Button>
          ) : (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={handleCancelEdit}
                sx={{ fontSize: '0.7rem', color: '#888', borderColor: '#ccc' }}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleSave}
                sx={{ fontSize: '0.7rem', backgroundColor: '#FF6A13',
                  '&:hover': { backgroundColor: '#F35627' } }}>
                Save Changes
              </Button>
            </Stack>
          )}
        </Box>

        <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField size="small" fullWidth label="PO Number" value={po.id}
              InputProps={{ readOnly: true }} disabled
              sx={{ '& input': { fontFamily: 'monospace', fontWeight: 700, color: FI_ORANGE } }} />
            <TextField size="small" fullWidth label="Supplier" value={supplier?.name ?? '—'}
              InputProps={{ readOnly: true }} disabled />

            <TextField size="small" fullWidth label="Date" type="date"
              InputLabelProps={{ shrink: true }}
              value={draft.date ?? ''}
              disabled={!editing}
              onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} />
            <TextField size="small" fullWidth label="Delivery date" type="date"
              InputLabelProps={{ shrink: true }}
              value={draft.deliveryDate ?? ''}
              disabled={!editing}
              onChange={e => setDraft(d => ({ ...d, deliveryDate: e.target.value }))} />

            <TextField size="small" fullWidth label="Job reference"
              value={draft.jobRef ?? ''}
              disabled={!editing}
              onChange={e => setDraft(d => ({ ...d, jobRef: e.target.value }))} />
            <TextField size="small" fullWidth label="Supplier reference"
              value={draft.reference ?? ''}
              disabled={!editing}
              onChange={e => setDraft(d => ({ ...d, reference: e.target.value }))} />

            <FormControl size="small" fullWidth disabled={!editing}>
              <InputLabel id="po-status-label">Status</InputLabel>
              <Select labelId="po-status-label" label="Status"
                value={draft.status ?? po.status}
                onChange={e => setDraft(d => ({ ...d, status: e.target.value as import('../data/types').PoStatus }))}>
                {PO_STATUS_OPTIONS.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box />

            <TextField size="small" fullWidth label="Notes" multiline minRows={2}
              value={draft.notes ?? ''}
              disabled={!editing}
              onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
              sx={{ gridColumn: '1 / -1' }} />
          </Box>
        </Paper>

        {/* Secondary actions */}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
            onClick={() => { onClose(); navigate(`/po-print/${po.id}`); }}
            sx={{ fontSize: '0.7rem', color: FI_ORANGE, borderColor: FI_ORANGE }}>
            Open full PO page
          </Button>
          <Box sx={{ flex: 1 }} />
        </Stack>
      </DialogContent>

      {/* Big Back button at the bottom too — unmissable exit */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee', backgroundColor: '#fafafa' }}>
        <Button variant="contained" size="large" fullWidth startIcon={<ArrowBackIcon />}
          onClick={onClose}
          sx={{ backgroundColor: BRAND, fontWeight: 800, fontSize: '0.9rem',
            py: 1.3, textTransform: 'none',
            '&:hover': { backgroundColor: '#4a3580' } }}>
          Back to Bill List
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Assign PO dialog (v1.14.4) ─────────────────────────────────────────────
// Surfaces the 4 explicit options the user asked for when a row has no
// confident match:
//   • Find an existing PO (by PO #)
//   • Find a Job (by jobRef)
//   • Find a Project (by project field on the PO)
//   • Create a brand-new PO
// One dialog, one focused autocomplete per tab, plus a soft "Did you mean…"
// suggestion when the matcher found a low-confidence candidate.
interface AssignPoDialogProps {
  item: QueueItem;
  purchaseOrders: Array<{ id: string; reference?: string; jobRef?: string; project?: string; total: number; status: string }>;
  onClose: () => void;
  onAssign: (poId: string) => void;
  onCreateNew: () => void;
}

const AssignPoDialog: React.FC<AssignPoDialogProps> = ({ item, purchaseOrders, onClose, onAssign, onCreateNew }) => {
  const [tab, setTab] = useState<'po' | 'job' | 'project'>('po');
  const [selectedPoId, setSelectedPoId] = useState<string | null>(item.suggestedPoId ?? null);

  const suggestion = item.suggestedPoId
    ? purchaseOrders.find(p => p.id === item.suggestedPoId)
    : undefined;

  // Build the per-tab option list. We always show POs; just label the
  // autocomplete differently and prioritise different fields when matching.
  const jobOptions = useMemo(
    () => purchaseOrders.filter(p => p.jobRef && p.jobRef.trim()),
    [purchaseOrders],
  );
  const projectOptions = useMemo(
    () => purchaseOrders.filter(p => p.project && (p.project as string).trim()),
    [purchaseOrders],
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HelpOutlineIcon sx={{ color: '#6a1b9a' }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>Assign a PO to this invoice</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>
              {item.parsed?.supplierName || '—'} · {item.parsed?.supplierInvoiceNo || item.fileName} · {item.parsed ? fmt(item.parsed.totalIncGst) : '—'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Soft suggestion when the matcher had a low-confidence candidate */}
        {suggestion && (
          <Box sx={{ p: 1.4, mb: 2, backgroundColor: '#fff8e1', borderRadius: 1.5, border: '1px solid #ffe082',
            display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: '#f57c00' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#5d4037' }}>
                Did you mean <Box component="span" sx={{ color: FI_ORANGE, fontFamily: 'monospace' }}>{suggestion.id}</Box>?
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: '#7d6e64' }}>
                {item.suggestedPoReason || 'Best supplier-name guess'} · {fmt(suggestion.total)} · job {suggestion.jobRef || '—'}
              </Typography>
            </Box>
            <Button size="small" variant="contained"
              onClick={() => onAssign(suggestion.id)}
              sx={{ textTransform: 'none', backgroundColor: '#6a1b9a', fontSize: '0.7rem',
                '&:hover': { backgroundColor: '#4a148c' } }}>
              Use this PO
            </Button>
          </Box>
        )}

        <ToggleButtonGroup
          value={tab} exclusive
          onChange={(_e, v) => v && setTab(v)}
          size="small" sx={{ mb: 2, width: '100%' }}>
          <ToggleButton value="po" sx={{ flex: 1, textTransform: 'none', fontSize: '0.72rem', py: 0.6 }}>
            By PO #
          </ToggleButton>
          <ToggleButton value="job" sx={{ flex: 1, textTransform: 'none', fontSize: '0.72rem', py: 0.6 }}>
            By Job #
          </ToggleButton>
          <ToggleButton value="project" sx={{ flex: 1, textTransform: 'none', fontSize: '0.72rem', py: 0.6 }}>
            By Project
          </ToggleButton>
        </ToggleButtonGroup>

        {tab === 'po' && (
          <Autocomplete size="small"
            options={purchaseOrders}
            value={purchaseOrders.find(p => p.id === selectedPoId) ?? null}
            onChange={(_e, val) => setSelectedPoId(val?.id ?? null)}
            getOptionLabel={p => `${p.id}${p.reference ? ` · ${p.reference}` : ''}`}
            renderOption={(props, p) => (
              <li {...props} key={p.id}>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: FI_ORANGE, fontFamily: 'monospace' }}>{p.id}</Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: '#888' }}>
                    {p.reference || '—'} · job {p.jobRef || '—'} · {fmt(p.total)} · {p.status}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={params => (
              <TextField {...params} label="Search by PO number or supplier reference" autoFocus />
            )} />
        )}

        {tab === 'job' && (
          <Autocomplete size="small"
            options={jobOptions}
            value={jobOptions.find(p => p.id === selectedPoId) ?? null}
            onChange={(_e, val) => setSelectedPoId(val?.id ?? null)}
            getOptionLabel={p => `Job ${p.jobRef} — ${p.id}`}
            renderOption={(props, p) => (
              <li {...props} key={p.id}>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#2e7d32' }}>Job {p.jobRef}</Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: '#888' }}>
                    {p.id} · {p.reference || '—'} · {fmt(p.total)} · {p.status}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={params => (
              <TextField {...params} label="Search by Job number" autoFocus
                helperText={jobOptions.length === 0 ? 'No POs have a Job # assigned yet' : undefined} />
            )} />
        )}

        {tab === 'project' && (
          <Autocomplete size="small"
            options={projectOptions}
            value={projectOptions.find(p => p.id === selectedPoId) ?? null}
            onChange={(_e, val) => setSelectedPoId(val?.id ?? null)}
            getOptionLabel={p => `${p.project} — ${p.id}`}
            renderOption={(props, p) => (
              <li {...props} key={p.id}>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: BRAND }}>{p.project}</Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: '#888' }}>
                    {p.id} · {p.reference || '—'} · job {p.jobRef || '—'} · {fmt(p.total)}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={params => (
              <TextField {...params} label="Search by Project" autoFocus
                helperText={projectOptions.length === 0 ? 'No POs have a Project assigned yet' : undefined} />
            )} />
        )}

        <Box sx={{ mt: 2, p: 1.2, backgroundColor: '#f3e5f5', borderRadius: 1.5, border: `1px solid #ce93d8`,
          display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon sx={{ fontSize: 18, color: '#6a1b9a' }} />
          <Typography sx={{ fontSize: '0.72rem', color: '#4a148c', flex: 1 }}>
            No PO exists for this invoice yet? Create one straight from the parsed line items.
          </Typography>
          <Button size="small" onClick={onCreateNew}
            sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#6a1b9a', fontWeight: 700 }}>
            Create new PO
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
        <Button variant="contained"
          disabled={!selectedPoId}
          onClick={() => selectedPoId && onAssign(selectedPoId)}
          sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: '#6a1b9a',
            '&:hover': { backgroundColor: '#4a148c' } }}>
          Link to {selectedPoId || 'PO'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
